import { createApi } from '@reduxjs/toolkit/query/react';
import {
  insertPosition,
  subtree,
} from '@/containers/sequences/sequence-tree.util';
import type { AgentEstimateBreakdown } from '@/types';
import { baseQueryWithAuth } from '../base-query';
import { expectedOrganizationHeaders } from '@/store/expected-organization-headers';
import { LIST_KEEP_SECONDS } from './list-cache';

// ── Types (mirror the cleanse-api engage responses; snake_case on the wire) ──
export type EngageStepKind =
  | 'email'
  | 'wait'
  | 'task'
  | 'linkedin_visit'
  | 'linkedin_invite'
  | 'linkedin_message'
  | 'handwritten_note'
  | 'branch'
  | 'play';

/** What a branch step checks to pick its Yes or No arm. */
export type EngageBranchSignal =
  | 'reply_label'
  | 'link_clicked'
  | 'invite_accepted'
  | 'call_outcome'
  | 'crm_field'
  | 'agent_score';

/** The decision of a branch step; each signal reads its own fields. */
export interface EngageBranchCondition {
  signal: EngageBranchSignal;
  /** Reply label: interested, objection or other. */
  labels?: string[];
  /** Link clicked: one link; empty or absent means any link. */
  url?: string;
  /** Call outcome: the outcomes that take Yes. */
  outcomes?: string[];
  /** CRM field and agent score: one field, an operator and a value. */
  field?: string;
  operator?: string;
  value?: string;
  /** The four event signals: hours to wait for it before taking No (0 to 720). */
  wait_hours?: number;
}

/** Who writes an email step: a member, or Clu from a brief. */
export type EngageStepWriter = 'manual' | 'clu';

/** What a Clu email may use, in the order the API lists them. */
export type EngageCluUseOption =
  | 'product_feature'
  | 'social_proof'
  | 'case_study'
  | 'pain_question'
  | 'their_news'
  | 'close_the_loop';

/** What Clu is asked to write, for a step Clu writes. */
export interface EngageStepBrief {
  /**
   * The step's skills: prebuilt keys ('product', 'proof', 'crm', 'research',
   * 'pain_question', 'close_the_loop') in that order, then up to 3 of the
   * team's skill ids in instruction order. A legacy step reads as the
   * skills its old setup gives.
   */
  skills: string[];
  /** Read only: the options the skills give the writer. */
  use?: EngageCluUseOption[];
  /** Up to 500 characters. */
  note: string;
  /** Legacy, no control: 30 to 300; null leaves the length to the organization's limit. */
  max_words: number | null;
  /** Legacy briefs: the API folds them into `note` (goal, a newline, the angle). */
  goal?: string;
  angle?: string;
  /** Legacy setting, read by the API as the Research skill. */
  research?: boolean;
}

export interface EngageStep {
  id: string;
  order_index: number;
  kind: EngageStepKind;
  title: string;
  subject: string | null;
  body: string | null;
  wait_days: number | null;
  instructions: string | null;
  task_type: string | null;
  /** Agent run on the lead when this step raises its task; null = none. */
  agent_key: string | null;
  writer: EngageStepWriter;
  /** Set when Clu writes the step. */
  brief: EngageStepBrief | null;
  /** The column the step sits in: '' is the trunk, 'y' and 'n' the arms of its branch. */
  arm_path: string;
  /** Branch steps: what decides the arm; null until one is chosen. */
  condition: EngageBranchCondition | null;
  /** Play steps: the play the lead is handed to. */
  play_id: string | null;
  play_name: string | null;
}

export type EngageSequenceStatus = 'Draft' | 'Active' | 'Paused' | 'Archived';

/** When a sequence may send: days (Monday = 0), hours, timezone, spacing. */
export interface EngageSchedule {
  timezone: string;
  days: number[];
  /** 24 hour 'HH:MM'. */
  start: string;
  end: string;
  interval_minutes: number;
  /** False until a member has saved the schedule themselves. */
  confirmed: boolean;
  /** Emails one sending day fits at this spacing. */
  daily_capacity: number;
  /**
   * The days and hours apply in each lead's own timezone, and their national
   * holidays are skipped. A lead with no known zone uses `timezone`.
   */
  lead_local: boolean;
}

export type EngageScheduleInput = Pick<
  EngageSchedule,
  'timezone' | 'days' | 'start' | 'end' | 'interval_minutes' | 'lead_local'
>;

export type EngageReplyRule = 'stop' | 'continue';

/** What happens to a draft Clu wrote: review each, the first, or none. */
export type EngageCluAutonomy = 'review' | 'approve_first' | 'auto';

export interface EngageStepSummary {
  /** Every step, waits included. */
  total: number;
  email: number;
  linkedin: number;
  /** Call tasks. */
  call: number;
}

export interface EngageSequence {
  id: string;
  name: string;
  owner_user_id: string;
  sender_mailbox_id: string | null;
  /**
   * The mailboxes the sequence sends from, in order. The first is
   * `sender_mailbox_id`; empty when the sequence has no sender yet.
   */
  sender_mailbox_ids: string[];
  /**
   * Who added each pool member and when, by mailbox id. `by` is null for a
   * mailbox added before this was recorded.
   */
  sender_added_by?: Record<string, { by: string | null; at: string | null }>;
  version: number;
  draft: boolean;
  draft_saved: boolean;
  dirty: boolean;
  paused: boolean;
  archived: boolean;
  status: EngageSequenceStatus;
  people: number;
  in_progress: number;
  /** Exact Completed/Pending run counts. List-only; absent on older responses. */
  completed?: number;
  pending?: number;
  /** Replied and Stopped runs, each exact. List-only. */
  replied?: number;
  stopped?: number;
  /** Clu drafts waiting or blocked on a running lead. List-only. */
  review_pending?: number;
  /**
   * What the steps add up to, by channel. The list leaves `steps` null (it
   * lists none) and carries this instead.
   */
  step_summary?: EngageStepSummary;
  steps: EngageStep[] | null;
  /** How many steps it has. List rows only; absent on the detail and older responses. */
  steps_count?: number;
  schedule: EngageSchedule;
  /** What a reply does to the lead's run: end it, or keep sending. */
  on_reply: EngageReplyRule;
  /** Raise a task for the owner when a lead replies. */
  reply_task: boolean;
  /** The connected LinkedIn account the LinkedIn steps act as. */
  linkedin_account_id: string | null;
  /** Per-day caps for this sequence; null = the account's own default. */
  linkedin_invite_limit: number | null;
  linkedin_message_limit: number | null;
  /** A lead running here may also run in other sequences that allow it. */
  allow_overlap: boolean;
  /** How the emails Clu writes here are reviewed. */
  clu_autonomy: EngageCluAutonomy;
  /** Send links through the tracked domain, so a click can branch a run. */
  track_clicks: boolean;
  /** Why the system paused the sequence; null when a member did, or it runs. */
  paused_reason: EngagePausedReason | null;
  /** The calling member's star. Absent on mutation responses (see API). */
  is_favorite?: boolean;
  /** All-time results; only the list carries them, like `is_favorite`. */
  results?: {
    contacted: number;
    replied: number;
    positive: number;
    meetings: number;
  };
  /** Leads a save moved onto the new steps; only the save answers with it. */
  moved?: number;
  created_at: string | null;
  modified_at: string | null;
}

/** An amount in one currency; null is the CRM's company currency. */
export interface EngageMoney {
  currency: string | null;
  amount: number;
}

/** What a sequence, one of its steps or one sender did in a range. */
export interface EngageResultCounts {
  sent: number;
  delivered: number;
  bounced: number;
  replied: number;
  positive: number;
  unsubscribed: number;
  meetings: number;
}

/** A step's row; the null row (`order_index` null) holds what has no step. */
export interface EngageResultStep extends EngageResultCounts {
  order_index: number | null;
  step_number: number | null;
  kind: EngageStepKind | null;
  title: string;
  /** False when the published version no longer has this step. */
  current: boolean;
  /** Tasks completed on this step. */
  completed: number;
}

/** A sender's row; the null row (`mailbox_id` null) holds what has no sender. */
export interface EngageResultSender extends EngageResultCounts {
  mailbox_id: string | null;
  email: string;
  name: string | null;
  provider: string | null;
  opportunities: number;
  pipeline: EngageMoney[];
}

/** GET /sequences/{id}/results: the funnel for a range of event times. */
export interface EngageSequenceResults {
  from: string | null;
  to: string | null;
  totals: EngageResultCounts & { contacted: number };
  steps: EngageResultStep[];
  senders: EngageResultSender[];
  /** Deals created on the leads' accounts; `crm` null when none is connected. */
  pipeline: {
    crm: EngageCrm | null;
    synced_at: string | null;
    opportunities: number;
    amounts: EngageMoney[];
  };
}

/** A day (or week, from its Monday) of a sequence's results. */
export interface EngageResultsPoint extends EngageResultCounts {
  /** The local date, `YYYY-MM-DD`. */
  date: string;
  /** People whose first send in the range fell on this day. */
  contacted: number;
}

/** GET /sequences/{id}/results/timeline: the range's counts per local day. */
export interface EngageResultsTimeline {
  from: string | null;
  to: string | null;
  tz: string;
  /** A range longer than about three months comes by week. */
  bucket: 'day' | 'week';
  points: EngageResultsPoint[];
}

export interface EngageMailbox {
  id: string;
  email_address: string;
  display_name: string;
  owner_user_id: string;
  provider: string; // 'google_workspace' | 'microsoft_365' | 'linkedin'
  connected: boolean;
  /** Owner/admin on-off switch: an inactive connection sends nothing. */
  active: boolean;
  shared: boolean;
  daily_limit: number;
  daily_used: number;
  /** Per-address signature override; null = the owner's default signature. */
  signature: string | null;
  /** Non-archived sequences sending from this connection (null when not computed). */
  used_in_sequences: number | null;
  /** LinkedIn rows only: what the account did today (null on email rows). */
  linkedin_used_today?: {
    invites: number;
    messages: number;
    visits: number;
  } | null;
  /**
   * When the reply webhook last delivered mail for this address; null when it
   * never has. Only the email-accounts list carries it.
   */
  last_inbound_at?: string | null;
  /** Why the system paused the connection; null when it is not paused. */
  paused_reason: EngagePausedReason | null;
  /** When the pause lifts; null with a reason means until someone resumes it. */
  paused_until: string | null;
  /** Why the system turned the connection off; absent on an older API. */
  disconnected_reason?: EngageDisconnectedReason | null;
  /** The sending domain's SPF, DKIM and DMARC; null before the first check. */
  domain_check: EngageDomainCheck | null;
  /**
   * Emails sent and hard bounces in the last 7 days, counted as the bounce
   * rate pause counts them. Null for LinkedIn, and on an answer about one
   * connection (the list carries them).
   */
  sent_7d?: number | null;
  bounced_7d?: number | null;
  /** LinkedIn rows only: what the account did in the last 7 days. */
  linkedin_used_week?: {
    invites: number;
    messages: number;
  } | null;
  created_at: string | null;
  modified_at: string | null;
}

/** Why the system paused a connection or a sequence. */
export type EngageDisconnectedReason =
  | 'plan_ended'
  | 'free_unused'
  | 'unused'
  | 'failed'
  | 'linkedin_requires_pro';

export type EngagePausedReason =
  | 'bounce_rate'
  | 'linkedin_limit'
  | 'linkedin_restricted'
  | 'linkedin_warning';

/** How a sending domain's records read: `provider` is a free mail domain. */
export type EngageDomainStatus = 'ok' | 'warn' | 'fail' | 'provider';

export interface EngageDomainRecords {
  status: EngageDomainStatus;
  /**
   * `no_provider`: a record that does not include the mailbox's provider.
   * `unknown`: the DNS read timed out. All three are null on a free mail
   * domain, which has no records of its own to read.
   */
  spf: 'pass' | 'missing' | 'no_provider' | 'unknown' | null;
  dkim: 'pass' | 'unknown' | null;
  dmarc: 'pass' | 'missing' | 'unknown' | null;
}

export interface EngageDomainCheck extends EngageDomainRecords {
  checked_at: string | null;
}

/** The hosted sign-in link, and the server clock to ask its outcome from. */
export interface EngageConnectLink {
  authorize_url: string;
  /** Epoch ms; absent from an older API. */
  started_at?: number;
  /** Server-generated identity for this sign-in, absent from an older API. */
  attempt_id?: string;
}

/**
 * How a hosted sign-in ended. `already_connected`: the address was stored and
 * healthy already (`owner_user_id` is whose). `refused`: it could not be
 * stored, with the API's `code` and `message` (the plan's sender limit).
 */
export type EngageConnectOutcome =
  | { status: 'pending' }
  | {
      status: 'connected' | 'already_connected';
      at: number;
      attempt_id?: string;
      mailbox_id: string;
      email_address: string;
      provider: string;
      owner_user_id: string;
    }
  | {
      status: 'refused';
      at: number;
      attempt_id?: string;
      code: string;
      message: string;
    };

/** LinkedIn accounts share the email-accounts list but never send email. */
export function isLinkedInAccount(account: Pick<EngageMailbox, 'provider'>) {
  return account.provider === 'linkedin';
}

/** What LinkedIn asks for before it lets a credentials sign-in through. */
export type LinkedInCheckpointType =
  | '2FA'
  | 'OTP'
  | 'IN_APP_VALIDATION'
  | 'CAPTCHA'
  | 'PHONE_REGISTER';

/** Every LinkedIn connect step answers with this: done, or another check. */
export type LinkedInConnectResult =
  | { status: 'connected'; account: EngageMailbox }
  | {
      status: 'checkpoint';
      checkpoint: LinkedInCheckpointType;
      /** Opaque, short-lived; sent back with the next checkpoint call. */
      session: string;
    };

export interface LinkedInConnectInput {
  username: string;
  password: string;
  /** Reconnect this existing LinkedIn account instead of adding one. */
  account_id?: string;
}

/**
 * What a change to a connection refreshes: the lists that show it, and the
 * dry runs on Launch, which read the sender's state.
 */
const mailboxChanged = ['Mailbox', 'LaunchPreview'] as const;

// Only a finished connect changes the accounts list.
const linkedInConnected = (result: LinkedInConnectResult | undefined) =>
  result?.status === 'connected' ? mailboxChanged : [];

/** A member's sender defaults (apply to every connection they own). */
export interface EngageSenderSettings {
  user_id: string;
  signature: string | null;
  /** Emails per day a newly connected address starts with. */
  default_daily_limit: number | null;
  /** LinkedIn connection requests per day (the saved value or the default). */
  linkedin_connection_limit: number;
  /** LinkedIn messages per day (the saved value or the default). */
  linkedin_message_limit: number;
  /** The link behind `{{sender.booking_link}}`; null when none is set. */
  booking_link: string | null;
}

/** The per-day limits a member can set, by settings field. */
export type EngageSenderLimitKey =
  | 'default_daily_limit'
  | 'linkedin_connection_limit'
  | 'linkedin_message_limit';

export type EngageSenderSettingsInput = {
  signature?: string | null;
  /** Empty or null clears the link. */
  booking_link?: string | null;
} & Partial<Record<EngageSenderLimitKey, number | null>>;

/** A picture stored for a signature, served at `url` to every mail client. */
export interface EngageSignatureImage {
  url: string;
  width: number;
  height: number;
}

/** Admin view: one member with their defaults and connections. */
export interface EngageTeamSender {
  user_id: string;
  settings: EngageSenderSettings;
  accounts: EngageMailbox[];
}

export interface EngageMailboxSettingsInput {
  id: string;
  display_name?: string;
  active?: boolean;
  shared?: boolean;
  daily_limit?: number;
  signature?: string | null;
  /** Lift a pause the system set. */
  resume?: true;
}

/** A sequence someone else owns that sends from a connection. */
export interface EngageMailboxSequence {
  id: string;
  name: string;
  owner_user_id: string;
  /** People assigned to this connection there and not finished. */
  people: number;
  only_sender: boolean;
}

export interface EngageEnrollment {
  id: string;
  lead_id: string | null;
  email: string;
  /** The lead's best number (mobile first); null when there is none. */
  phone?: string | null;
  name: string;
  company: string | null;
  title: string | null;
  /**
   * The contact snapshot a send merges from, so a preview fills a lead's
   * variables the way the send does.
   */
  first_name?: string | null;
  last_name?: string | null;
  location?: string | null;
  linkedin_url?: string | null;
  company_domain?: string | null;
  company_industry?: string | null;
  company_employee_count?: number | null;
  company_location?: string | null;
  status: string; // Active | Replied | Completed | Stopped
  step_label: string;
  /** The order of the step the lead is on in its pinned revision. */
  step_index: number | null;
  sender_mailbox_id: string | null;
  version: number;
  created_at: string | null;
  /** Why an Active run is held; null when it is not. */
  hold_reason: EngageHoldReason | null;
  /** When the hold lifts; null with a reason means until its cause clears. */
  hold_until: string | null;
  /** Why and when the run ended; null while it runs. */
  end_reason: EngageEndReason | null;
  ended_at: string | null;
  /** 0 for the first address; 1 once a bounce found the lead a new one. */
  send_round: number;
  /** Who paused the run, and why; null when it is not paused by a member. */
  paused_by_user_id: string | null;
  pause_note: string | null;
  /** The order of the step a member told the run to skip; null when none. */
  skip_order_index: number | null;
  /** Why the run sends from its mailbox; null before one is chosen. */
  sender_reason: EngageSenderReason | null;
  /** What added the lead to the sequence; null when it was not recorded. */
  trigger: EngageTrigger | null;
  /** When the next email goes; null when nothing is queued. */
  scheduled_send_at?: string | null;
}

/** Why a run sends from the mailbox it does. */
export type EngageSenderReason = 'crm_owner' | 'rotation' | 'only' | 'fallback';

/** What enrolled a lead: a play, a search, a list, a person, Clu or the API. */
export interface EngageTrigger {
  source:
    | 'play'
    | 'find_people'
    | 'list'
    | 'csv'
    | 'moved'
    | 'manual'
    | 'clu'
    | 'api';
  /** The play's trigger kind, e.g. `job_change`; null for the other sources. */
  kind: string | null;
  /** How the lead row reads it, e.g. `Job change`. */
  label: string;
  play_name: string | null;
  /** The lead list a whole-list add came from. */
  list_id?: string | null;
  /** The member who added the lead; absent on a run the engine started. */
  added_by_user_id?: string | null;
  /** The values `{{trigger.<key>}}` reads. */
  fields: Record<string, unknown>;
}

/** Why a running lead is held before its next step. */
export type EngageHoldReason =
  | 'out_of_office'
  | 'bounce_repair'
  | 'crm_activity'
  | 'account_reply'
  | 'manual'
  | 'field_fill'
  | 'draft_review';

/** What a member can do to one lead (see `actOnLead`). */
export type EngageLeadAction =
  | 'pause'
  | 'snooze'
  | 'resume'
  | 'skip'
  | 'remove'
  | 'meeting_booked';

/** What an action did, in every sequence the person runs in. */
export interface EngageLeadActionResult {
  applied: {
    enrollment_id: string;
    sequence_id: string;
    sequence_name: string;
    result:
      | 'paused'
      | 'snoozed'
      | 'resumed'
      | 'skip_set'
      | 'removed'
      | 'meeting_booked';
  }[];
  skipped: {
    enrollment_id: string;
    sequence_id: string;
    sequence_name: string;
    reason: 'not_active' | 'not_paused' | 'nothing_to_skip';
  }[];
}

/** What a bulk action did: every run's rows joined, and the runs not found. */
export interface EngageBulkLeadActionResult extends EngageLeadActionResult {
  not_found: number;
}

/** What a move did: leads started in the target, and the ones left in place. */
export interface EngageMoveResult {
  moved: number;
  skipped: { id: string; name: string; reason: string }[];
  excluded: EligibilityRow[];
}

/** Why a lead's run ended. */
export type EngageEndReason =
  | 'completed'
  | 'replied'
  | 'not_interested'
  | 'referral'
  | 'left_company'
  | 'unsubscribed'
  | 'bounced'
  | 'out_of_office'
  | 'send_failed'
  | 'crm_opt_out'
  | 'call_outcome'
  | 'sequence_archived'
  | 'play_value_missing'
  | 'active_elsewhere'
  | 'crm_check'
  | 'do_not_contact'
  | 'removed'
  | 'meeting_booked'
  | 'play_removed'
  | 'agent_step';

/** What an inbound reply was read as. */
export type EngageReplyLabel =
  | 'interested'
  | 'objection'
  | 'referral'
  | 'not_interested'
  | 'left_company'
  | 'unsubscribe'
  | 'out_of_office'
  | 'bounce_notice'
  | 'other';

/** The end reasons a lead waits out before another sequence may take them. */
export type EngageReentryReason = Exclude<
  EngageEndReason,
  'unsubscribed' | 'bounced' | 'left_company' | 'crm_opt_out' | 'do_not_contact'
>;

/**
 * The organization's sequencing options: replies and bounces, and safe
 * sending. A numeric rule set to zero is off.
 */
export interface EngageSequencingSettings {
  /** Find a new email after a hard bounce. Off until an admin turns it on. */
  bounce_repair: boolean;
  /** Credits bounce repair may spend in one UTC day. */
  bounce_repair_daily_credits: number;
  bounce_repair_credits_today: number;
  /** The organization's kill switch: nothing sends while it is on. */
  sending_stopped: boolean;
  sending_stopped_at: string | null;
  sending_stopped_by: string | null;
  /** The postal address every email's footer carries. Launch needs it. */
  company_address: string;
  /** Stop a lead the CRM has as a customer. */
  crm_check_customer: boolean;
  /** Stop a lead with an open deal in the CRM. */
  crm_check_open_deal: boolean;
  /** Stop a lead another rep owns in the CRM. */
  crm_check_other_owner: boolean;
  /** Hold a lead a teammate touched in the CRM this many days ago. */
  crm_check_activity_days: number;
  /** A reply holds everyone else at that company. */
  account_pause_on_reply: boolean;
  account_pause_days: number;
  /** People emailed at one company in one day. */
  company_daily_cap: number;
  /** Days after a run ends, by why it ended, before another may start. */
  reentry_days: Partial<Record<EngageReentryReason, number>>;
  /** Check an address again before the first email when older than this. */
  reverify_after_days: number;
  /** Emails a mailbox sends to catch-all addresses in one day. */
  catch_all_daily_per_mailbox: number;
  linkedin_weekly_invites: number;
  linkedin_weekly_messages: number;
  /** Pause a mailbox and its sequence at the bounce limit below. */
  bounce_auto_pause: boolean;
  /** Pause a mailbox and its sequence at this hard bounce rate over 7 days. */
  bounce_pause_percent: number;
  /** Sends in 7 days before the bounce rate counts. */
  bounce_pause_min_sent: number;
  /** A booked meeting ends the lead's run. */
  meeting_ends_run: boolean;
  /** Skip an email or LinkedIn step whose merge field is empty; off, the
   * field is sent blank. */
  skip_empty_fields: boolean;
  /** Look up an empty merge field before a step skips. Only while skipping. */
  fill_empty_fields: boolean;
  /** Credits filling may spend in one UTC day. */
  fill_empty_fields_daily_credits: number;
  fill_empty_fields_credits_today: number;
  /** Clu's hard rules, checked in code over every draft. */
  clu_banned_phrases: string[];
  clu_max_words: number;
  clu_required_cta: string[];
  clu_excluded_titles: string[];
  clu_excluded_accounts: string[];
  /** Clu emails one lead's run may be sent; 0 is no limit. */
  clu_max_touches: number;
  /** Tone for the prompt only; never checked. */
  clu_style: string;
  /** Emails the team wrote, which Clu reads for its voice. Never checked. */
  clu_voice_samples: string[];
  /** Credits Clu may spend on one lead's run, and in one UTC day. */
  clu_lead_credit_cap: number;
  clu_daily_credit_cap: number;
  clu_credits_today: number;
  /** Knowledge base sources Clu may answer replies from. */
  clu_reply_source_ids: string[];
  /** Approved sources marked Case study: a subset of `clu_reply_source_ids`. */
  clu_case_study_source_ids: string[];
  clu_rules_version: number;
  clu_reply_sources: EngageCluReplySource[];
  /** What Clu may cite in a sequence email. */
  clu_content: EngageCluContent;
  /**
   * Clu email steps in the organization's live sequences that use each
   * writing skill, by builtin key or skill id. A key with no step may be left
   * out; an older API sends none.
   */
  clu_skill_steps?: Record<string, number>;
  /** Hours a positive reply waits before the owner is told; 0 is off. */
  positive_reply_alert_hours: number;
  /** Hours before the admins are told as well; 0 is off. */
  positive_reply_escalate_hours: number;
  /** Days a step task may stay open before it expires; 0 is never. */
  task_expire_days: number;
  /** Org-wide, off until an admin enables automatic task lead scoring. */
  auto_score_tasks?: boolean;
  auto_score_stopped_at?: string | null;
  /** End a run whose lead a stored Employment check says left the company. */
  job_change_stop: boolean;
  /** Runs wait for a new owner when the sequence's or the sender's owner
   * leaves the workspace; off, they keep sending. */
  owner_left_hold: boolean;
  /** Sequences created before these rules shipped follow them too; off,
   * they keep the earlier behaviour. */
  older_sequences_follow_rules: boolean;
  /** Admins only. */
  editable: boolean;
}

/**
 * Clu's email for one lead, written as a test: shown with each rule's result
 * and never stored, charged or sent. `excluded` is a lead the hard rules
 * keep Clu from writing to; it has no email.
 */
export interface EngageCluTestResult {
  status: 'written' | 'excluded';
  lead: { name: string; title: string | null; company: string | null };
  subject: string;
  body: string;
  grounded: boolean;
  rules: EngageRuleResult[];
  passed: boolean;
  failed: string[];
  facts: { id: string; text: string; kind: string }[];
  voice_samples: number;
  model: string | null;
}

/**
 * Counts of what Clu may cite: profile products and value props, customer
 * proof items, case study sources still active and approved, and approved,
 * active sources that are not call notes. An API without `documents` yet
 * reads as none.
 */
export interface EngageCluContent {
  product_details: number;
  customer_proof: number;
  case_studies: number;
  documents?: number;
}

export interface EngageCluReplySource {
  id: string;
  name: string;
  kind: string;
  approved: boolean;
}

/** A setting left out keeps its stored value. */
export type EngageSequencingSettingsInput = Partial<
  Omit<
    EngageSequencingSettings,
    | 'bounce_repair_credits_today'
    | 'fill_empty_fields_credits_today'
    | 'clu_credits_today'
    | 'clu_rules_version'
    | 'clu_reply_sources'
    | 'clu_content'
    | 'clu_skill_steps'
    | 'sending_stopped_at'
    | 'sending_stopped_by'
    | 'auto_score_stopped_at'
    | 'editable'
  >
>;

/** What a do-not-contact entry matches on. */
export type EngageDncKind =
  | 'email'
  | 'domain'
  | 'company'
  | 'linkedin'
  | 'phone'
  | 'crm_id';

/** What an entry blocks: every step, email steps, or call tasks. */
export type EngageDncScope = 'all' | 'email' | 'call';

/** Where an entry came from. */
export type EngageDncSource =
  | 'unsubscribe_link'
  | 'inbox'
  | 'reply'
  | 'crm:salesforce'
  | 'crm:hubspot'
  | 'manual'
  | 'import';

export interface EngageDncEntry {
  id: string;
  kind: EngageDncKind;
  /** Normalized: a lowercased email, a registrable domain, an E.164 phone. */
  value: string;
  scope: EngageDncScope;
  source: EngageDncSource;
  reason: string | null;
  created_at: string | null;
  created_by_user_id: string | null;
  /** False for unsubscribes, replies and CRM opt-outs, which stay. */
  removable: boolean;
}

export interface EngageDncPage {
  items: EngageDncEntry[];
  /** Pass back as `cursor` for the next page; null on the last. */
  next_cursor: string | null;
  /**
   * How many entries the list holds, by kind (a kind with none is absent).
   * Only the first page of the whole list, with no kind or search, has it.
   */
  counts?: Partial<Record<EngageDncKind, number>>;
}

export interface EngageDncInput {
  kind: EngageDncKind;
  value: string;
  scope?: EngageDncScope;
  reason?: string;
}

export interface EngageDncAddResult {
  added: number;
  /** Already on the list. */
  existing: number;
  invalid: { value: string; error: string }[];
}

/** People in one group of a launch's dry run, with up to 5 of them named. */
export interface EngageLaunchGroup {
  code: string;
  label: string;
  count: number;
  examples: EngageLaunchExample[];
}

export interface EngageLaunchExample {
  enrollment_id: string;
  name: string;
  email: string;
}

/** What launching a draft would do to the leads waiting in it. */
export interface EngageLaunchPreview {
  /** Leads waiting to start. */
  total: number;
  will_send: number;
  held: EngageLaunchGroup[];
  blocked: EngageLaunchGroup[];
  /** Merge fields a step uses that are empty for some leads. */
  empty_fields: {
    step_number: number;
    field: string;
    count: number;
    examples: EngageLaunchExample[];
  }[];
  duplicates: { count: number; examples: EngageLaunchExample[] };
  role_addresses: { count: number; examples: EngageLaunchExample[] };
  /** Addresses checked again before the first email; free. */
  reverify: { count: number; credits: number };
  /** Leads with no cached CRM record to check. */
  crm_unchecked: number;
  /** The sender's domain; null when the sequence sends no email. */
  domain: EngageDomainRecords | null;
  company_address: boolean;
  /** What Clu's emails cost the leads waiting; null with no Clu step. */
  clu: EngageLaunchClu | null;
  /** When the people who will send start and finish; null with none. */
  forecast: EngageLaunchForecast | null;
  /** The first message for up to three of the people who will send. */
  samples: EngageLaunchSample[];
}

/** One day of the send plan: emails that leave on it, all steps together. */
export interface EngageLaunchDay {
  /** ISO date. */
  date: string;
  count: number;
}

export interface EngageLaunchForecast {
  /** People who will send. */
  leads: number;
  /** ISO instant the first message could leave. */
  first_send_at: string;
  /** The timezone of the first lead to send, to read `first_send_at` in. */
  first_send_zone: string;
  /** Emails a day the senders allow; null when it is not an email launch. */
  per_day: number | null;
  /** Senders that can send now; null when it is not an email launch. */
  senders: number | null;
  /** ISO date every lead's first email is out; null when not known. */
  first_done_on: string | null;
  /** ISO date the last email of the last step is out; null when not known. */
  done_on: string | null;
  /** The number of the last email step. */
  done_step: number | null;
  /** The emails a day, from the first day (up to 60 days). */
  daily: EngageLaunchDay[];
  daily_truncated: boolean;
}

export interface EngageLaunchField {
  /** `lead.job_title`. */
  token: string;
  /** "job title". */
  label: string;
}

/** The first message of the draft, for one lead, as they would receive it. */
export interface EngageLaunchSample {
  enrollment_id: string;
  name: string;
  email: string;
  company: string;
  channel: 'email' | 'linkedin';
  step_number: number | null;
  writer: 'you' | 'clu';
  /** The account it would send as; null when none is usable. */
  sender: { name: string; email: string } | null;
  subject: string | null;
  /** The whole email, with signature and footer. Email only. */
  body_html: string | null;
  /** LinkedIn only. */
  body_text: string | null;
  /** Fields the message reads with no fallback that are empty for this lead. */
  empty_fields: EngageLaunchField[];
  /** Fields that are empty here and show their fallback text. */
  fallbacks: EngageLaunchField[];
  /** What a send does about an empty field: skip the step, look it up, or
   * send it blank (an older sequence, or skipping turned off). */
  on_empty: 'skip' | 'fill' | 'blank' | null;
  /** A Clu step: written at send, or a checked draft that already exists. */
  clu: {
    state: 'at_send' | 'drafted';
    status: 'waiting' | 'approved' | null;
    sources: number;
  } | null;
}

export interface EngageLaunchClu {
  /** Steps Clu writes. */
  steps: number;
  leads: number;
  write_credits_per_lead: number;
  /** Research charged per lead for this launch, zero when no Clu step has it. */
  research_credits_per_lead: number;
  research_credits_per_run: number;
  /** Research's price per lead, whether a step has it on or not. */
  research_price_per_lead?: number;
  /** How Research's price per lead is estimated, before a run rounds it up
   * to `research_price_per_lead`. */
  research_estimate?: AgentEstimateBreakdown;
  limit_warning?: string | null;
  /** What each Clu step costs a lead, keyed by the step's `order_index`. */
  write_credits_by_step: Record<string, number>;
  /** How each step's price is estimated, keyed like `write_credits_by_step`. */
  write_estimate_by_step?: Record<string, AgentEstimateBreakdown>;
  /** Leads times the per lead sum, each lead held to `lead_cap`. */
  credits: number;
  lead_cap: number;
  daily_cap: number;
  /** Days at the daily cap. */
  days: number;
}

// ── Clu drafts ──────────────────────────────────────────────────────────────
export type EngageDraftStatus =
  | 'drafting'
  | 'waiting'
  | 'blocked'
  | 'approved'
  | 'rejected';

/** Where a fact came from. */
export type EngageDraftSourceKind =
  | 'record'
  | 'research'
  | 'reply'
  | 'play'
  | 'trigger'
  | 'document'
  | 'proof'
  | 'crm'
  | 'deal';

export interface EngageDraftFact {
  id: string;
  text: string;
  date: string | null;
  source: {
    kind: EngageDraftSourceKind;
    ref: string | null;
    url: string | null;
    title?: string | null;
  };
}

/** A statement in the draft; `fact_id` is null when nothing backs it. */
export interface EngageDraftClaim {
  text: string;
  fact_id: string | null;
}

export interface EngageRuleResult {
  rule: string;
  ok: boolean;
  detail: string;
}

/** One of the sequence's sample leads, ordered by fact count by the API. */
export interface EngageCluSampleLead {
  enrollment_id: string;
  name: string;
  title: string;
  company: string;
  facts: number;
}

export interface EngageCluSampleSent {
  order_index: number;
  subject: string;
  body: string;
}

/** A charged sample, never stored or sent. Research may still be running. */
export interface EngageCluSample {
  lead: Omit<EngageCluSampleLead, 'facts'>;
  order_index: number;
  step_number: number;
  follow_up: boolean;
  status: 'written' | 'empty' | 'excluded' | 'researching';
  retry_after?: number;
  subject: string;
  body: string;
  rules: EngageRuleResult[];
  passed: boolean;
  failed: string[];
  use: EngageCluUseOption[];
  used: Partial<Record<EngageCluUseOption, string[]>>;
  facts: {
    id: string;
    text: string;
    kind: EngageDraftSourceKind;
    date: string | null;
  }[];
  credits: number;
  left_today: number;
}

export interface EngageDraft {
  id: string;
  kind: 'step' | 'reply';
  status: EngageDraftStatus;
  sequence_id: string | null;
  sequence_name: string | null;
  enrollment_id: string | null;
  step_order_index: number | null;
  step_number: number | null;
  send_round: number;
  lead: {
    name: string;
    email: string;
    company: string;
    title: string;
  };
  subject: string;
  body: string;
  edited: boolean;
  facts: EngageDraftFact[];
  claims: EngageDraftClaim[];
  rule_results: EngageRuleResult[];
  rules_version: number;
  model: string;
  autonomy: EngageCluAutonomy | null;
  credits: number;
  research_credits: number;
  created_at: string;
  decided_by_user_id: string | null;
  decided_at: string | null;
  decision_reason: string | null;
  /** When the email went out; null before. */
  sent_at: string | null;
}

/** What a step draft was written with, as the draft records it. */
export interface EngageDraftWriting {
  skills?: { id: string; name: string; version: number | null }[];
  skills_missing?: string[];
  /** The options ticked on the step. */
  use?: EngageCluUseOption[];
  /** The facts each ticked option used; an empty list is one it had none for. */
  used?: Partial<Record<EngageCluUseOption, string[]>>;
}

/** One draft as `GET /sequences/drafts/{id}` reads it. */
export interface EngageDraftDetail extends EngageDraft {
  lead_state?: { writing?: EngageDraftWriting | null } | null;
}

export interface EngageDraftPage {
  items: EngageDraft[];
  /** Pass back as `cursor` for the next page; null on the last. */
  next_cursor: string | null;
  /** Step drafts still to decide; a state with none may be left out. */
  counts: { waiting?: number; blocked?: number };
}

/**
 * A draft a member has to decide, without its evidence (the pending list is
 * for finding and opening them; the evidence is `EngageDraft`).
 */
export interface EngagePendingDraft {
  id: string;
  status: 'waiting' | 'blocked';
  sequence_id: string;
  sequence_name: string;
  enrollment_id: string;
  step_order_index: number | null;
  step_number: number | null;
  lead: EngageDraft['lead'];
  subject: string;
  /** The first characters of the email. */
  preview: string;
  /** The rules that failed; empty for a draft that passes them all. */
  failed_rules: string[];
  edited: boolean;
  created_at: string;
  /** The sequence's owner or an org admin may approve and reject it. */
  can_decide: boolean;
}

/** How many drafts wait in one sequence. */
export interface EngagePendingSequence {
  id: string;
  name: string;
  waiting: number;
  blocked: number;
  can_decide: boolean;
}

export interface EngagePendingDraftPage {
  /** Oldest first. */
  items: EngagePendingDraft[];
  next_cursor: string | null;
  /** Waiting and blocked in the same scope as the items: the organization,
   *  the caller's own sequences with `mine`, or the one `sequence_id`. */
  counts: { waiting?: number; blocked?: number };
  /** The sequences with drafts to decide, most first; the first page only. */
  sequences?: EngagePendingSequence[];
}

export interface EngageDecideDraftsInput {
  action: 'approve' | 'reject';
  draft_ids: string[];
  /** The drafts the member saw; the API decides nothing if they changed. */
  expected_count?: number;
  reason?: { text: string };
  /** New text per draft id. */
  edits?: Record<string, { subject: string; body: string }>;
}

export interface EngageDecideDraftsResult {
  approved: number;
  rejected: number;
  refused: {
    id: string;
    error: 'rule_failed' | 'already_decided' | 'not_found';
    rule_results?: EngageRuleResult[];
  }[];
}

/** A reply Clu wrote from approved documents. */
export interface EngageThreadDrafted {
  status: 'drafted';
  draft_id: string;
  body: string;
  documents: {
    document_id: string;
    title: string;
    url: string | null;
    source: string;
  }[];
  rule_results: EngageRuleResult[];
  credits: number;
}

/** A reply Clu wrote from approved documents, or nothing to write it from. */
export type EngageThreadDraftResult =
  | EngageThreadDrafted
  | { status: 'no_match' };

/**
 * The reply Clu wrote when a message arrived, not yet sent. `auto` is true
 * when Clu wrote it unasked.
 */
export type EngageThreadClu = EngageThreadDrafted & { auto: boolean };

/** The meeting a reply asks for; `when` is the time in their own words. */
export interface EngageMeetingRequest {
  when: string | null;
}

/** The task and the reply the one click for a meeting request gives. */
export interface EngageBookMeetingResult {
  task_id: string;
  created: boolean;
  when: string | null;
  reply: string;
}

export interface EngageTask {
  id: string;
  sequence_id: string;
  enrollment_id: string | null;
  name: string;
  person: string;
  company: string;
  email: string;
  type: string; // Call | Manual | Reply
  due_date: string | null;
  time24: string | null;
  assignee_user_id: string | null;
  status: 'open' | 'completed' | 'expired';
  priority: EngageTaskPriority;
  /** Why the task ranks where it does, best first; empty outside My tasks. */
  signals: EngageTaskSignal[];
  /** 1-based place in My tasks, in rank order; absent in other lists. */
  rank?: number;
  /** The agent the step asked for, the run it started, or why it could not. */
  agent_key: string | null;
  agent_run_id: string | null;
  agent_error: string | null;
  /** A sequence step, an inbound reply, or a rep's own follow-up. */
  origin: 'step' | 'reply' | 'follow_up';
  outcome: string;
  notes: string;
  /** The step's instructions (for a call, the script), variables filled in. */
  instructions: string | null;
  /** Which action step raised the task (waits are not counted). */
  step_number: number | null;
  /** An email Reply task's Inbox conversation, where it can be answered. */
  thread_id?: string | null;
  completed_at: string | null;
  completed_by_user_id: string | null;
  /** Call log, recorded when a call task is completed. */
  call_phone: string | null;
  call_started_at: string | null;
  call_duration_seconds: number | null;
  sequence_name: string | null;
  /** The lead's job title, profile and place, from their run. */
  title: string | null;
  linkedin_url: string | null;
  location: string | null;
  company_domain: string | null;
  lead_id: string | null;
  /** The lead's numbers, best first (mobile, then direct lines). Call tasks only. */
  phones: EngagePhone[];
  /** The lead's photo, from their lead record. */
  profile_pic_url: string | null;
  /** IANA zone the lead's location settles on, for their local time. */
  timezone: string | null;
  /** The lead's newest Qualification result, when one has run. */
  fit: {
    score: number;
    verdict: string | null;
    /** The lead's recent scores, oldest first, ending on the current one. */
    history?: { score: number; at: string | null }[];
  } | null;
  created_at: string | null;
  blocked_reason: string;
  actionable: boolean;
  /** The lead's run behind the task, as the lead menu needs it; null when none. */
  run: EngageTaskRun | null;
}

/** What a task knows of its run: enough to draw the lead's menu. */
export interface EngageTaskRun {
  status: string;
  hold_reason: EngageHoldReason | null;
  hold_until: string | null;
}

export type EngageTaskPriority = 'High' | 'Medium' | 'Low';

/** What lifts a task in My tasks, best first. */
export type EngageTaskSignal =
  | 'positive_reply'
  | 'reply'
  | 'open_deal'
  | 'job_change';

export interface EngageRunManyResult {
  runs?: {
    task_id: string;
    agent_task_id: string;
    run_id: string;
    lead_list_id: string;
  }[];
  started: string[];
  skipped: {
    id: string;
    reason:
      | 'completed'
      | 'no_lead'
      | 'same_lead'
      | 'has_result'
      | 'running'
      | 'rate_limited'
      | 'no_credits'
      | string;
  }[];
  /** Tasks that could not start this time; press play again in a minute. */
  remaining: number;
  /** Tasks that would run (dry run) or did run. */
  tasks: number;
  credits: number;
  stale?: number;
}

export interface EngageScheduleSettings {
  /** Null while the built-in default is in force. */
  schedule: EngageSchedule | null;
  default: EngageSchedule;
  can_edit: boolean;
  last_edited_by_user_id: string | null;
}

export interface EngageBulkTaskResult {
  updated: string[];
  skipped: { id: string; reason: 'not_found' | 'completed' | 'forbidden' }[];
}

/** A lead in one of the org's sequences: who a task can be created for. */
export interface EngageTaskLead {
  enrollment_id: string;
  name: string;
  email: string;
  title: string | null;
  company: string | null;
  sequence_name: string;
}

/** An agent a task can run on its lead, and the credits one run costs. */
export interface EngageTaskAgent {
  /** research | qualification | custom:<id> */
  key: string;
  name: string;
  credits: number;
}

export interface EngageAgentResult {
  key: string;
  name: string;
  markdown: string;
  structured: Record<string, unknown> | null;
  lead_list_id: string;
  updated_at: string | null;
}

export interface EngageTaskInsights {
  lead_id: string | null;
  results: EngageAgentResult[];
  /** Agent keys with a run in flight. */
  running: string[];
}

export interface EngageCallOutcomeOption {
  value: string;
  /** The CRM's own value for it (a HubSpot disposition id, a picklist value). */
  crm_value: string | null;
  ends_sequence: boolean;
}

export interface EngageCallOutcomeSettings {
  source: 'cleanlist' | 'crm' | 'dialer';
  crm: 'hubspot' | 'salesforce' | null;
  dialer: 'trellus' | null;
  object: string | null;
  field: string | null;
  field_label: string | null;
  outcomes: EngageCallOutcomeOption[];
  presets: EngageCallOutcomeOption[];
  connected_crms: ('hubspot' | 'salesforce')[];
  connected_dialers: 'trellus'[];
  can_edit: boolean;
}

export interface EngageCrmCallField {
  object: string;
  name: string;
  label: string;
  options: { value: string; label: string }[];
}

export type EngageCrm = 'salesforce' | 'hubspot';

/**
 * A sequence event the CRM settings' Activities tab turns on or off, by its
 * setting key. A Salesforce Task Type is chosen per event with the same key.
 */
export type EngageCrmActivityEvent =
  | 'emails'
  | 'replies'
  | 'calls'
  | 'linkedin_messages'
  | 'linkedin_invites'
  | 'linkedin_visits'
  | 'manual_tasks';

/**
 * The organization's sequence activity settings for its CRM, one set for both
 * CRMs (`engage_org_settings.crm_activity`). Every write goes to `crm`.
 */
export interface EngageCrmActivitySettings {
  /** Log sequence activity. Off until an admin turns it on. */
  enabled: boolean;
  /** The "Log to" CRM; null until one is connected. */
  crm: EngageCrm | null;
  emails: boolean;
  replies: boolean;
  calls: boolean;
  linkedin_messages: boolean;
  linkedin_invites: boolean;
  linkedin_visits: boolean;
  manual_tasks: boolean;
  /** Stored for bounce capture, which has no toggle yet. */
  bounces: boolean;
  /** Mirror open call, manual and reply tasks as open CRM tasks. */
  open_tasks: boolean;
  /** Salesforce Task Type per event. A missing event keeps the default rule. */
  sf_task_types: Partial<Record<EngageCrmActivityEvent, string>>;
  /** Salesforce Task record type id; null is the org default. */
  sf_record_type_id: string | null;
  /** Cleanlist sequence fields on the CRM person. */
  person_fields: boolean;
  honor_opt_outs: boolean;
  /** Write the unsubscribes we capture to the CRM, with logging on or off. */
  write_unsubscribes: boolean;
  /** Create a person missing from the CRM, as the General tab's record type. */
  create_missing: boolean;
  create_when: 'first_touch' | 'reply';
}

export interface EngageCrmActivitySettingsRead {
  settings: EngageCrmActivitySettings;
  connected_crms: EngageCrm[];
  can_edit: boolean;
}

/** Whether a CRM can take activity writes, for the tab's header. */
export type EngageCrmActivitySetupState =
  | 'connected'
  | 'needs_reconnect'
  | 'setup_needed'
  | 'not_connected'
  | 'unavailable';

/** One CRM's state, and the Salesforce values the tab's selects offer. */
export interface EngageCrmActivitySetup {
  crm: EngageCrm;
  state: EngageCrmActivitySetupState;
  /** HubSpot scopes the install lacks. */
  missing_scopes: string[];
  task_types: { value: string; label: string }[];
  record_types: { id: string; name: string; default: boolean }[];
}

/** Where one outbox row is, as the list shows and filters it. */
export type EngageCrmActivityState =
  | 'logged'
  | 'waiting'
  | 'not_in_crm'
  | 'failed'
  | 'skipped';

/** One row of the CRM activity outbox, as the Activities tab lists it. */
export interface EngageCrmActivityRow {
  id: string;
  /** email | reply | rep_reply | call | linkedin_* | manual | task | state | create */
  kind: string;
  /** The subject the CRM shows for it. */
  title: string;
  person: { name: string; email: string };
  lead_id: string | null;
  actor_user_id: string | null;
  sequence: string | null;
  occurred_at: string;
  updated_at: string | null;
  state: EngageCrmActivityState;
  /** Why the row is where it is, e.g. "Logged as fallback owner". */
  reason: string | null;
  /** The CRM's own message on a failed attempt. */
  error: string | null;
  crm_url: string | null;
  /** The caller is an admin and the row failed. */
  can_retry: boolean;
}

export interface EngageCrmActivityPage {
  crm: EngageCrm | null;
  activities: EngageCrmActivityRow[];
  /** Pass back as `cursor` for the next, older page; null on the last. */
  next_cursor: string | null;
}

/**
 * CRM users the CRM refused as inactive owners, so the activity of the reps
 * mapped to them was logged as the fallback owner.
 */
export interface EngageCrmInactiveOwners {
  crm: EngageCrm | null;
  owners: {
    crm_user_id: string;
    refused_at: string;
    /** The Cleanlist users mapped to that CRM user. */
    user_ids: string[];
    /** The fallback row is mapped to it. */
    fallback: boolean;
  }[];
}

/** Someone else at a task's company who is in one of the org's sequences. */
export interface EngageAccountPerson {
  name: string;
  title: string | null;
  email: string;
  lead_id: string | null;
  sequence_id: string;
  sequence_name: string;
  status: string;
  step_label: string;
  last_call_outcome: string | null;
}

/** What our company catalog knows about a lead's employer. A field is null
 *  when no provider has filled it. */
export interface EngageCompanyFacts {
  name: string | null;
  domain: string | null;
  website: string | null;
  linkedin_url: string | null;
  tagline: string | null;
  description: string | null;
  industry: string | null;
  company_type: string | null;
  founded_year: number | null;
  headquarters: string | null;
  employees: number | null;
  employees_min: number | null;
  employees_max: number | null;
  employee_growth_6m: number | null;
  employee_growth_12m: number | null;
  open_jobs: number | null;
  revenue_range: string | null;
  total_funding_usd: number | null;
  last_round_type: string | null;
  last_round_usd: number | null;
  last_round_date: string | null;
  valuation_usd: number | null;
  investors: string[];
  investor_count: number;
  stock_symbols: string[];
  acquisition_status: string | null;
  monthly_visitors: number | null;
}

export interface EngageCrmOwner {
  id: string | null;
  name: string | null;
  email: string | null;
}

export interface EngageCrmDeal {
  id: string;
  name: string;
  stage: string;
  amount: number | null;
  close_date: string | null;
  is_closed: boolean;
  is_won: boolean;
  owner: EngageCrmOwner | null;
  url: string | null;
}

export interface EngageCrmActivity {
  id: string | null;
  /** task | event | note | call | email | meeting */
  type: string | null;
  subject: string;
  status: string | null;
  occurred_at: string | null;
  owner: EngageCrmOwner | null;
  /** Who at the account it was with, when the CRM says. */
  contact: string | null;
  body: string | null;
}

/**
 * The account's side of the connected CRM: its record, owner, activity and
 * deals. The LEAD's CRM status is not here on purpose. It comes from the CRM
 * status endpoints in `crm.feature`, the same ones the lists use.
 */
export interface EngageTaskAccountCrm {
  crm: 'salesforce' | 'hubspot' | null;
  /** Why there is nothing to show, e.g. "not_connected". */
  reason: string | null;
  account: {
    record_id: string | null;
    record_url: string | null;
    name: string | null;
    owner: EngageCrmOwner | null;
  } | null;
  activities: EngageCrmActivity[];
  deals: EngageCrmDeal[];
  /** Sections that could not be read: account | deals | activities | budget | crm. */
  warnings: string[];
}

export interface EngageTaskAccount {
  people: EngageAccountPerson[];
  company: EngageCompanyFacts | null;
}

export interface EngagePhone {
  number: string;
  /** The data provider's own label: mobile, direct, landline, work, unknown... */
  type: string;
}

/** One thing that happened to a lead in a sequence. */
export interface EngageTimelineEvent {
  kind:
    | 'added'
    | 'email'
    | 'call'
    | 'task'
    | 'task_open'
    | 'reply_in'
    | 'reply_out'
    | 'linkedin'
    | 'bounce'
    | 'auto_reply'
    | 'hold'
    | 'release'
    | 'address'
    | 'unsubscribe'
    | 'stopped'
    | 'linkedin_reply'
    | 'skip'
    | 'meeting'
    | 'draft'
    | 'version'
    | 'handover'
    | 'task_expired'
    | 'branch'
    | 'invite_accepted'
    | 'click'
    | 'play';
  at: string | null;
  title: string;
  detail: string;
  /** Set on tasks: still open, or done. */
  status?: 'open' | 'done';
  /** Set on replies in: what the reply was read as. */
  label?: EngageReplyLabel | null;
  /** Set on a lead's timeline across sequences: which run the row is from. */
  sequence_id?: string;
  sequence_name?: string;
  /** Set on a call made in Trellus (the call row the read-back stored), or
   * placed with Cleanlist's own dialer. */
  call?: EngageTrellusCall | EngageCleanlistCall;
}

/** A call placed (or a callback taken) with Cleanlist's own dialer. Its
 * recording streams from `GET /telephony/calls/{callId}/recording`. */
export interface EngageCleanlistCall {
  source: 'cleanlist';
  callId: string;
  direction: 'outbound' | 'inbound' | 'voicemail';
  status: string;
  disposition: string | null;
  durationSeconds: number | null;
  notes: string | null;
  hasRecording: boolean;
  transcript: string | null;
}

export interface EngageTrellusCall {
  source: 'trellus';
  sessionId: string;
  disposition: string | null;
  durationSeconds: number | null;
  notes: string | null;
  hasRecording: boolean;
  transcript: string | null;
}

export interface EngageCallOutcome {
  value: string;
  /** Completing a call with this outcome ends the lead's sequence. */
  ends_sequence: boolean;
}

export type EngageJobChangeState =
  | 'none'
  | 'running'
  | 'found'
  | 'not_found'
  | 'unavailable';

/** What is known of a lead who left the company a run ended on. */
export interface EngageJobChange {
  state: EngageJobChangeState;
  old: {
    company: string | null;
    title: string | null;
    email: string | null;
  } | null;
  /** Null until the Employment check found where the lead went. */
  new: {
    company: string | null;
    title: string | null;
    email: string | null;
    email_status: string | null;
    linkedin_url: string | null;
  } | null;
  checked_at: string | null;
  /** The most credits Find new email can spend. */
  credits_max: number;
  error: string | null;
  /** The run this lead was added to at the new address; null until then. */
  enrolled: {
    sequence_id: string;
    sequence_name: string;
    enrollment_id: string;
  } | null;
  /** Find People filters for the lead's old seat. */
  replacement: {
    currentCompanyDomains?: string[];
    jobTitlesInclude?: string[];
  } | null;
}

export interface EngageThreadMessage {
  direction: 'in' | 'out';
  body: string;
  at: string | null;
  /** What an inbound message was read as; null on ours. */
  label: EngageReplyLabel | null;
  /** The meeting this message asks for, when it does. */
  meeting?: EngageMeetingRequest | null;
}

export interface EngageThread {
  id: string;
  sequence_id: string;
  name: string;
  email: string;
  company: string;
  subject: string;
  text: string;
  last_message_at: string | null;
  needs_reply: boolean;
  /** What the newest reply was read as; null before one is. */
  label: EngageReplyLabel | null;
  reply: string;
  sent: { body: string; at: string }[];
  /** The conversation in order. */
  messages: EngageThreadMessage[];
  enrollment_id: string | null;
  mailbox_id: string | null;
  /** The sequence and step the lead is on, and who owns the sequence. */
  sequence_name: string | null;
  sequence_owner_user_id: string | null;
  on_reply: EngageReplyRule | null;
  step_label: string | null;
  lead_status: string | null;
  /** The lead record, when the run came from a lead list. */
  lead_id: string | null;
  lead_title: string | null;
  /** The address a reply leaves from, and the signature it carries. */
  sender_email: string | null;
  sender_signature: string | null;
  /**
   * Whether this member can send from that address, and if not why: no
   * sender, not theirs to use, turned off or in need of a reconnect. Absent
   * on an older API, which reads as `ok`.
   */
  sender_state?: EngageThreadSenderState;
  /** The sentence for a sender that cannot be used. */
  sender_message?: string | null;
  /**
   * The person unsubscribed from the org's sequences, by the link in a
   * sequence email or from the Inbox.
   */
  unsubscribed: boolean;
  /** Who answers it; null until someone does. */
  assignee_user_id: string | null;
  /** Hidden from the open list until then. */
  snoozed_until: string | null;
  archived_at: string | null;
  /** The reply Clu wrote for the latest message, not yet sent. */
  clu_draft?: EngageThreadClu | null;
  /** Whether the plan lets Clu write replies (Pro and up). */
  clu_writing?: boolean;
  /** The meeting the latest message asks for, until someone answers. */
  meeting_request?: EngageMeetingRequest | null;
}

/** Whether the viewer can send from the conversation's address. */
export type EngageThreadSenderState =
  | 'ok'
  | 'none'
  | 'forbidden'
  | 'off'
  | 'reconnect';

/** Where a conversation sits in the Inbox. */
export type EngageThreadState = 'open' | 'snoozed' | 'archived';

/** The keys sent change; the ones left out stay as they are. */
export interface EngageThreadChange {
  assignee_user_id?: string | null;
  snoozed_until?: string | null;
  archived?: boolean;
  /** A correction of how the newest reply was read; null clears it. */
  label?: EngageReplyLabel | null;
}

/** How many conversations each Inbox view holds under the same filters. */
export interface EngageThreadCounts {
  needs: number;
  all: number;
  snoozed: number;
  archived: number;
}

/** The filters the Inbox list and its counts share. */
export interface EngageThreadFilters {
  sequence_id?: string;
  mailbox_id?: string;
  /** YYYY-MM-DD: only conversations with a message on or after it. */
  since?: string;
  /** Only conversations whose newest reply was read as this; `none`: unread. */
  label?: EngageReplyLabel | 'none';
  /** A user id, `me` or `unassigned`. */
  assignee?: string;
  /** Text in the name, address, company, subject, sequence or a message. */
  q?: string;
  organizationId?: string;
  userId?: string;
}

/** A published edit: the sequence, and how many running leads it reaches. */
export type EngageSaveResult = EngageSequence & { reaches?: number };

/**
 * When the leads an edit reaches have moved. The save answers before they do:
 * each lead moves when its own workflow wakes, so what the leads, drafts and
 * tasks show is read again a few times after the answer.
 */
export const EDIT_LANDS_AFTER_MS = [2_000, 6_000, 15_000] as const;

/** Whose work is left behind: a member who left, or a mailbox that is down. */
export interface EngageHandoverCandidates {
  members: {
    user_id: string;
    sequences: number;
    running: number;
    open_tasks: number;
    threads: number;
    linkedin_waiting?: number;
    mailbox_ids: string[];
  }[];
  mailboxes: {
    mailbox_id: string;
    email: string;
    owner_user_id: string;
    state: 'disconnected' | 'off';
    running: number;
  }[];
}

/** At least one of `from_user_id` and `from_mailbox_id`. */
export interface EngageHandoverInput {
  from_user_id?: string | null;
  from_mailbox_id?: string | null;
  to_user_id: string;
  to_mailbox_id: string;
  /** Count what would move and write nothing. */
  dry_run: boolean;
}

export interface EngageHandoverResult {
  dry_run: boolean;
  sequences: number;
  enrollments: number;
  tasks: number;
  threads: number;
}

export interface EngageContact {
  id: string;
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  title?: string;
  location?: string;
  linkedin_url?: string;
  /** The lead's first phone, for call steps. */
  phone?: string;
  company_domain?: string;
  company_industry?: string;
  company_employee_count?: number;
  company_location?: string;
  quality?: string;
  suppressed?: boolean;
  existing?: boolean;
}

export interface EligibilityRow {
  id: string;
  name: string;
  reason: string;
  /** False for a lead ready by LinkedIn or phone alone, with no email. */
  has_email?: boolean;
}

// ── API ─────────────────────────────────────────────────────────────────────
/** Placeholder step shown while the server creates the real one. */
/** Ids of optimistic placeholders; the server has not created these rows yet. */
export const PENDING_PREFIX = 'pending-';

export function isPendingStep(step: { id: string }): boolean {
  return step.id.startsWith(PENDING_PREFIX);
}

function pendingStep(
  id: string,
  kind: EngageStepKind,
  taskType?: string,
  armPath = ''
): EngageStep {
  return {
    id,
    order_index: 0,
    kind,
    title: '',
    subject: kind === 'email' ? '' : null,
    body: kind === 'email' || kind === 'handwritten_note' ? '' : null,
    wait_days: kind === 'wait' ? 2 : null,
    instructions: kind === 'task' ? '' : null,
    task_type: kind === 'task' ? (taskType ?? 'Manual') : null,
    agent_key: null,
    writer: 'manual',
    brief: null,
    arm_path: armPath,
    condition: null,
    play_id: null,
    play_name: null,
  };
}

/**
 * Take from a save's response only the fields that save sent. Two saves on one
 * step can be in flight together (the script saving on blur while the type is
 * switched): each response carries the whole step as the server saw it then, so
 * replacing the step would put the other save's field back to its old value
 * and the card would flip between the two.
 */
export function mergeSavedStepFields(
  step: EngageStep,
  saved: EngageStep,
  keys: string[]
): void {
  const target = step as unknown as Record<string, unknown>;
  const source = saved as unknown as Record<string, unknown>;
  for (const key of keys) if (key in source) target[key] = source[key];
}

/** The browser's IANA zone, which the local calendar days above are in. */
const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * A step change patches the open sequence's cache, so the one read left to
 * refresh is the dry run on Launch, which counts what the steps would do.
 */
const stepsChanged = (
  _result: unknown,
  _error: unknown,
  { sequenceId }: { sequenceId: string }
) => [{ type: 'LaunchPreview' as const, id: sequenceId }];

export const engageApi = createApi({
  reducerPath: 'engageApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'Sequence',
    'Mailbox',
    'SenderSettings',
    'Task',
    'Thread',
    'Enrollment',
    'LaunchPreview',
    'TaskInsights',
    'CallOutcomes',
    'OrgSchedule',
    'CrmActivitySettings',
    'CrmActivitySetup',
    'CrmActivity',
    'EngageSequencingSettings',
    'DoNotContact',
    'SequenceResults',
    'Draft',
    'Handover',
  ],
  endpoints: (builder) => ({
    // Sequences
    listSequences: builder.query<
      EngageSequence[],
      {
        q?: string;
        status?: string;
        owner_user_id?: string;
        organizationId?: string;
        /** Cache partition for an in-place signed-in user change. */
        userId?: string;
        /** Rows without their step bodies (`steps` is null, `steps_count` says how many). */
        include_steps?: boolean;
      } | void
    >({
      keepUnusedDataFor: LIST_KEEP_SECONDS,
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg?.q) params.append('q', arg.q);
        if (arg?.status) params.append('status', arg.status);
        if (arg?.owner_user_id)
          params.append('owner_user_id', arg.owner_user_id);
        // The library lists no steps, which were most of the payload. A
        // caller that reads them asks with include_steps: true.
        if (arg?.include_steps !== true)
          params.append('include_steps', 'false');
        const qs = params.toString();
        return {
          url: `/sequences?${qs}`,
          method: 'GET',
          ...(arg?.organizationId
            ? {
                headers: { 'X-Cleanlist-Expected-Org': arg.organizationId },
              }
            : {}),
        };
      },
      providesTags: [{ type: 'Sequence', id: 'LIST' }],
    }),
    getSequence: builder.query<EngageSequence, string>({
      query: (id) => ({ url: `/sequences/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Sequence', id }],
    }),
    createSequence: builder.mutation<
      EngageSequence,
      { name: string; timezone?: string }
    >({
      query: (body) => ({ url: `/sequences`, method: 'POST', body }),
      invalidatesTags: ['Sequence', 'Mailbox'],
    }),
    updateSequence: builder.mutation<
      EngageSequence,
      {
        id: string;
        name?: string;
        sender_mailbox_id?: string | null;
        /** 1 to 10 mailboxes in order; the first becomes the primary. */
        sender_mailbox_ids?: string[];
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/sequences/${id}`,
        method: 'PATCH',
        body,
      }),
      // The open editor is patched in place (optimistically, then from the
      // response); only the list and the dry run on Launch refetch. A sender
      // change also changes each mailbox's "used in sequences" count.
      invalidatesTags: (
        _result,
        _error,
        { id, sender_mailbox_id, sender_mailbox_ids }
      ) => [
        { type: 'Sequence', id: 'LIST' },
        { type: 'LaunchPreview', id },
        ...(sender_mailbox_id === undefined && sender_mailbox_ids === undefined
          ? []
          : (['Mailbox'] as const)),
      ],
      async onQueryStarted({ id, ...fields }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          engageApi.util.updateQueryData('getSequence', id, (draft) => {
            Object.assign(draft, fields);
            // The API keeps the primary and the pool in step: a pool's first
            // id is the primary, and a lone primary is a pool of one.
            if (fields.sender_mailbox_ids?.length)
              draft.sender_mailbox_id = fields.sender_mailbox_ids[0];
            else if (fields.sender_mailbox_id !== undefined)
              draft.sender_mailbox_ids = fields.sender_mailbox_id
                ? [fields.sender_mailbox_id]
                : [];
            draft.dirty = true;
            draft.draft_saved = false;
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            engageApi.util.updateQueryData('getSequence', id, (draft) => {
              // Never the response's steps: a step added or edited while this
              // request was in flight lives only in the cache, and replacing
              // the array would drop it.
              const kept = draft.steps;
              Object.assign(draft, data);
              draft.steps = kept;
            })
          );
        } catch {
          patch.undo();
        }
      },
    }),
    deleteSequence: builder.mutation<void, string>({
      query: (id) => ({ url: `/sequences/${id}`, method: 'DELETE' }),
      // The settings count the Clu steps of live sequences per skill.
      invalidatesTags: [
        'Sequence',
        'Mailbox',
        'Draft',
        'EngageSequencingSettings',
      ],
    }),
    /**
     * Takes a mailbox off a sequence's senders, live or draft: the one click
     * its owner gets when a teammate added it. Its leads move to another
     * sender or wait for one. The mailbox's owner or an admin.
     */
    removeSequenceSender: builder.mutation<
      void,
      { sequenceId: string; mailboxId: string }
    >({
      query: ({ sequenceId, mailboxId }) => ({
        url: `/sequences/${sequenceId}/senders/${mailboxId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Sequence', 'Mailbox', 'Enrollment'],
    }),
    duplicateSequence: builder.mutation<EngageSequence, string>({
      query: (id) => ({ url: `/sequences/${id}/duplicate`, method: 'POST' }),
      invalidatesTags: ['Sequence', 'Mailbox'],
    }),
    editSequence: builder.mutation<EngageSequence, string>({
      query: (id) => ({ url: `/sequences/${id}/edit`, method: 'POST' }),
      invalidatesTags: ['Sequence'],
    }),
    cancelEditSequence: builder.mutation<EngageSequence, string>({
      query: (id) => ({ url: `/sequences/${id}/edit/cancel`, method: 'POST' }),
      invalidatesTags: ['Sequence'],
    }),
    /**
     * Publishes the edit. Every lead already running picks it up: what was
     * sent stays sent, and the step each lead is on and the steps after it
     * follow the edit. The answer's `reaches` says how many leads that is.
     */
    saveSequence: builder.mutation<EngageSaveResult, { id: string }>({
      query: ({ id }) => ({ url: `/sequences/${id}/save`, method: 'POST' }),
      invalidatesTags: (result) => [
        'Sequence',
        // A published edit can change which skills its Clu steps use.
        'EngageSequencingSettings',
        ...(result?.reaches ? (['Enrollment'] as const) : []),
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data.reaches) return;
          for (const after of EDIT_LANDS_AFTER_MS)
            setTimeout(
              () =>
                dispatch(
                  engageApi.util.invalidateTags([
                    'Enrollment',
                    'Draft',
                    'Task',
                    'SequenceResults',
                  ])
                ),
              after
            );
        } catch {
          // A save that failed reaches nobody.
        }
      },
    }),
    activateSequence: builder.mutation<
      EngageSequence,
      { id: string; expected_version?: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/sequences/${id}/activate`,
        method: 'POST',
        body,
      }),
      // A live sequence's Clu steps count toward each skill's usage.
      invalidatesTags: ['Sequence', 'Enrollment', 'EngageSequencingSettings'],
    }),
    pauseSequence: builder.mutation<EngageSequence, string>({
      query: (id) => ({ url: `/sequences/${id}/pause`, method: 'POST' }),
      invalidatesTags: ['Sequence'],
    }),
    resumeSequence: builder.mutation<EngageSequence, string>({
      query: (id) => ({ url: `/sequences/${id}/resume`, method: 'POST' }),
      invalidatesTags: ['Sequence'],
    }),
    /** An archived sequence comes back paused; resuming is a separate call. */
    unarchiveSequence: builder.mutation<EngageSequence, string>({
      query: (id) => ({ url: `/sequences/${id}/unarchive`, method: 'POST' }),
      invalidatesTags: ['Sequence'],
    }),
    archiveSequence: builder.mutation<EngageSequence, string>({
      query: (id) => ({ url: `/sequences/${id}/archive`, method: 'POST' }),
      invalidatesTags: [
        'Sequence',
        'Mailbox',
        'Draft',
        'EngageSequencingSettings',
      ],
    }),

    // Star a sequence for the current member. Patched optimistically into the
    // open sequence and every cached list, and rolled back if the call fails.
    // Sequence settings save in any state and apply live, so they are their
    // own endpoint (not part of the step draft that `updateSequence` edits).
    updateSequenceSettings: builder.mutation<
      EngageSequence,
      {
        id: string;
        schedule?: EngageScheduleInput;
        on_reply?: EngageReplyRule;
        reply_task?: boolean;
        /** Omitted = unchanged; null = clear (the limits fall back to the account's). */
        linkedin_account_id?: string | null;
        linkedin_invite_limit?: number | null;
        linkedin_message_limit?: number | null;
        allow_overlap?: boolean;
        clu_autonomy?: EngageCluAutonomy;
        track_clicks?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/sequences/${id}/settings`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Sequence', id: 'LIST' },
        { type: 'LaunchPreview', id },
      ],
      async onQueryStarted(
        {
          id,
          schedule,
          on_reply,
          reply_task,
          allow_overlap,
          clu_autonomy,
          ...linkedin
        },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          engageApi.util.updateQueryData('getSequence', id, (draft) => {
            if (schedule)
              draft.schedule = {
                ...draft.schedule,
                ...schedule,
                confirmed: true,
              };
            if (on_reply) draft.on_reply = on_reply;
            if (reply_task !== undefined) draft.reply_task = reply_task;
            if (allow_overlap !== undefined)
              draft.allow_overlap = allow_overlap;
            if (clu_autonomy) draft.clu_autonomy = clu_autonomy;
            for (const [key, value] of Object.entries(linkedin))
              if (value !== undefined) Object.assign(draft, { [key]: value });
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            engageApi.util.updateQueryData('getSequence', id, (draft) => {
              draft.schedule = data.schedule;
              draft.on_reply = data.on_reply;
              draft.reply_task = data.reply_task;
              draft.linkedin_account_id = data.linkedin_account_id ?? null;
              draft.linkedin_invite_limit = data.linkedin_invite_limit ?? null;
              draft.linkedin_message_limit =
                data.linkedin_message_limit ?? null;
              draft.allow_overlap = data.allow_overlap;
              draft.clu_autonomy = data.clu_autonomy;
              draft.track_clicks = data.track_clicks;
              draft.modified_at = data.modified_at;
            })
          );
        } catch {
          patch.undo();
        }
      },
    }),
    setSequenceFavorite: builder.mutation<
      { id: string; is_favorite: boolean },
      { id: string; is_favorite: boolean }
    >({
      query: ({ id, is_favorite }) => ({
        url: `/sequences/${id}/favorite`,
        method: 'PUT',
        body: { is_favorite },
      }),
      async onQueryStarted(
        { id, is_favorite },
        { dispatch, queryFulfilled, getState }
      ) {
        const patches = [
          dispatch(
            engageApi.util.updateQueryData('getSequence', id, (draft) => {
              draft.is_favorite = is_favorite;
            })
          ),
        ];
        const lists = engageApi.util.selectInvalidatedBy(
          getState() as Parameters<
            typeof engageApi.util.selectInvalidatedBy
          >[0],
          [{ type: 'Sequence', id: 'LIST' }]
        );
        for (const entry of lists) {
          if (entry.endpointName !== 'listSequences') continue;
          patches.push(
            dispatch(
              engageApi.util.updateQueryData(
                'listSequences',
                entry.originalArgs as { q?: string; status?: string } | void,
                (draft) => {
                  const row = draft.find((item) => item.id === id);
                  if (row) row.is_favorite = is_favorite;
                }
              )
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),

    // Steps. These patch the open sequence's cache instead of invalidating it:
    // a blanket invalidation cost a POST plus two refetches (~2.5 s against a
    // remote database) before a new step appeared. The optimistic entry is
    // swapped for the server's step when the response lands, or removed on
    // failure. Only the dry run on Launch is invalidated.
    addStep: builder.mutation<
      EngageStep & { delay: EngageStep | null },
      {
        sequenceId: string;
        kind: Exclude<EngageStepKind, 'play'>;
        /**
         * The order of the step to insert after, in the column `arm_path`
         * names; -1 or none is the head of that column.
         */
        insert_after?: number;
        /** The column: '' is the trunk, 'y' and 'n' the arms of its branch. */
        arm_path?: string;
        /** Set a task step's type in the same call. */
        task_type?: string;
        /** Put a wait of this many business days directly before the step. */
        delay_days?: number;
      }
    >({
      query: ({ sequenceId, ...body }) => ({
        url: `/sequences/${sequenceId}/steps`,
        method: 'POST',
        body,
      }),
      invalidatesTags: stepsChanged,
      async onQueryStarted(
        { sequenceId, kind, insert_after, arm_path, task_type, delay_days },
        { dispatch, queryFulfilled }
      ) {
        const stamp = Date.now();
        const pendingId = PENDING_PREFIX + stamp;
        const pendingDelayId = PENDING_PREFIX + 'wait-' + stamp;
        const patch = dispatch(
          engageApi.util.updateQueryData('getSequence', sequenceId, (draft) => {
            const steps = draft.steps ?? [];
            const position = insertPosition(
              steps,
              arm_path ?? '',
              insert_after ?? -1
            );
            const added = [
              pendingStep(pendingId, kind, task_type, arm_path ?? ''),
            ];
            if (delay_days && kind !== 'wait')
              added.unshift({
                ...pendingStep(pendingDelayId, 'wait', undefined, arm_path),
                wait_days: delay_days,
              });
            steps.splice(position, 0, ...added);
            steps.forEach((step, index) => (step.order_index = index));
            draft.steps = steps;
            draft.dirty = true;
          })
        );
        try {
          const { data } = await queryFulfilled;
          const { delay, ...created } = data;
          dispatch(
            engageApi.util.updateQueryData(
              'getSequence',
              sequenceId,
              (draft) => {
                const steps = draft.steps ?? [];
                const index = steps.findIndex((step) => step.id === pendingId);
                if (index >= 0) steps[index] = created;
                else steps.push(created);
                const delayIndex = steps.findIndex(
                  (step) => step.id === pendingDelayId
                );
                if (delay && delayIndex >= 0) steps[delayIndex] = delay;
                draft.steps = steps;
              }
            )
          );
        } catch {
          patch.undo();
        }
      },
    }),
    updateStep: builder.mutation<
      EngageStep,
      {
        sequenceId: string;
        stepId: string;
        title?: string;
        subject?: string;
        body?: string;
        wait_days?: number;
        instructions?: string;
        task_type?: string;
        /** Explicitly clear unsupported saved automatic agent execution. */
        agent_key?: '';
        /** Email steps only. */
        writer?: EngageStepWriter;
        brief?: EngageStepBrief;
        /** Branch steps only. */
        condition?: EngageBranchCondition;
      }
    >({
      query: ({ sequenceId, stepId, ...body }) => ({
        url: `/sequences/${sequenceId}/steps/${stepId}`,
        method: 'PUT',
        body,
      }),
      // A Clu step's skills are counted in the sequencing settings
      // (`clu_skill_steps`), which Skills > Writing reads.
      invalidatesTags: (result, error, change) => [
        ...stepsChanged(result, error, change),
        ...(change.brief !== undefined || change.writer !== undefined
          ? (['EngageSequencingSettings'] as const)
          : []),
      ],
      async onQueryStarted(
        { sequenceId, stepId, ...fields },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          engageApi.util.updateQueryData('getSequence', sequenceId, (draft) => {
            const step = draft.steps?.find((item) => item.id === stepId);
            if (step) Object.assign(step, fields);
          })
        );
        try {
          const { data: updated } = await queryFulfilled;
          dispatch(
            engageApi.util.updateQueryData(
              'getSequence',
              sequenceId,
              (draft) => {
                const step = draft.steps?.find((item) => item.id === stepId);
                if (!step) return;
                // A new writer changes more than was sent (Clu clears the
                // subject and the message, You clears the brief): take the
                // whole step the server holds.
                if (fields.writer !== undefined) Object.assign(step, updated);
                else mergeSavedStepFields(step, updated, Object.keys(fields));
              }
            )
          );
        } catch {
          patch.undo();
        }
      },
    }),
    deleteStep: builder.mutation<void, { sequenceId: string; stepId: string }>({
      query: ({ sequenceId, stepId }) => ({
        url: `/sequences/${sequenceId}/steps/${stepId}`,
        method: 'DELETE',
      }),
      invalidatesTags: stepsChanged,
      async onQueryStarted(
        { sequenceId, stepId },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          engageApi.util.updateQueryData('getSequence', sequenceId, (draft) => {
            if (!draft.steps) return;
            // A branch goes with both its arms.
            const branch = draft.steps.find((item) => item.id === stepId);
            const gone = new Set([
              stepId,
              ...(branch?.kind === 'branch'
                ? subtree(draft.steps, branch).map((item) => item.id)
                : []),
            ]);
            draft.steps = draft.steps.filter((item) => !gone.has(item.id));
            draft.steps.forEach((step, index) => (step.order_index = index));
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    reorderSteps: builder.mutation<
      EngageStep[],
      { sequenceId: string; step_ids: string[] }
    >({
      query: ({ sequenceId, step_ids }) => ({
        url: `/sequences/${sequenceId}/steps/reorder`,
        method: 'PUT',
        body: { step_ids },
      }),
      invalidatesTags: stepsChanged,
      async onQueryStarted(
        { sequenceId, step_ids },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          engageApi.util.updateQueryData('getSequence', sequenceId, (draft) => {
            if (!draft.steps) return;
            const byId = new Map(draft.steps.map((step) => [step.id, step]));
            const ordered = step_ids
              .map((id) => byId.get(id))
              .filter((step): step is EngageStep => Boolean(step));
            ordered.forEach((step, index) => (step.order_index = index));
            draft.steps = ordered;
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            engageApi.util.updateQueryData(
              'getSequence',
              sequenceId,
              (draft) => {
                draft.steps = data;
              }
            )
          );
        } catch {
          patch.undo();
        }
      },
    }),

    // Enrollment
    previewEnrollment: builder.mutation<
      { rows: EligibilityRow[]; ready: number; excluded: number },
      // Either explicit `contacts` (a table selection / search result) OR a
      // `list_id` to enroll a whole lead list (the server resolves + hydrates it).
      {
        sequenceId: string;
        contacts?: EngageContact[];
        list_id?: string;
        /** Leave out emails that are not verified or likely valid. */
        verified_only?: boolean;
        /** false skips the live CRM opt-out read (the enroll still makes it),
         *  for a caller polling while emails are found. */
        crm_check?: boolean;
      }
    >({
      query: ({ sequenceId, contacts, list_id, verified_only, crm_check }) => ({
        url: `/sequences/${sequenceId}/enrollments/preview`,
        method: 'POST',
        body: {
          ...(list_id ? { list_id } : { contacts: contacts ?? [] }),
          ...(verified_only ? { verified_only: true } : {}),
          ...(crm_check === false ? { crm_check: false } : {}),
        },
      }),
    }),
    enroll: builder.mutation<
      {
        requested: number;
        enrolled_ids: string[];
        excluded: EligibilityRow[];
        /** Added to a draft while their email is still being found (Pending
         *  until launch reads it); sent back when `await_email` is. */
        awaiting_email_ids?: string[];
      },
      {
        sequenceId: string;
        contacts?: EngageContact[];
        list_id?: string;
        sender_mailbox_id?: string | null;
        version: number;
        source?: string;
        verified_only?: boolean;
        /** Keep people whose email is still being found: a draft adds them as
         *  Pending; a live sequence leaves them out as Missing email. */
        await_email?: boolean;
      }
    >({
      query: ({ sequenceId, ...body }) => ({
        url: `/sequences/${sequenceId}/enrollments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { sequenceId }) => [
        'Sequence',
        'Enrollment',
        { type: 'LaunchPreview', id: sequenceId },
      ],
    }),
    listEnrollments: builder.query<
      EngageEnrollment[],
      { sequenceId: string; status?: string }
    >({
      keepUnusedDataFor: LIST_KEEP_SECONDS,
      query: ({ sequenceId, status }) => {
        const qs = status ? `?status=${encodeURIComponent(status)}` : '';
        return {
          url: `/sequences/${sequenceId}/enrollments${qs}`,
          method: 'GET',
        };
      },
      providesTags: ['Enrollment'],
    }),
    /**
     * Pause, snooze, resume, skip, remove or mark a meeting on one lead. Pause,
     * snooze, resume and remove reach every run of the person, so the answer
     * lists each sequence they touched.
     */
    actOnLead: builder.mutation<
      EngageLeadActionResult,
      {
        sequenceId: string;
        enrollmentId: string;
        action: EngageLeadAction;
        /** The day a snooze ends, 'YYYY-MM-DD'. Snooze only. */
        until?: string;
        /** Why, up to 300 characters. */
        note?: string;
      }
    >({
      query: ({ sequenceId, enrollmentId, ...body }) => ({
        url: `/sequences/${sequenceId}/enrollments/${enrollmentId}/actions`,
        method: 'POST',
        body,
      }),
      // An ended run drops its waiting Clu draft from the review queue.
      invalidatesTags: (_result, _error, { sequenceId }) => [
        'Enrollment',
        'Task',
        'Sequence',
        'Draft',
        { type: 'SequenceResults', id: sequenceId },
      ],
    }),
    /** One action on many leads of a sequence (the Leads tab's selection bar). */
    bulkActOnLeads: builder.mutation<
      EngageBulkLeadActionResult,
      {
        sequenceId: string;
        enrollmentIds: string[];
        action: EngageLeadAction;
        /** The day a snooze ends, 'YYYY-MM-DD'. Snooze only. */
        until?: string;
        note?: string;
      }
    >({
      query: ({ sequenceId, enrollmentIds, ...body }) => ({
        url: `/sequences/${sequenceId}/enrollments/bulk-actions`,
        method: 'POST',
        body: { ...body, enrollment_ids: enrollmentIds },
      }),
      invalidatesTags: (_result, _error, { sequenceId }) => [
        'Enrollment',
        'Task',
        'Sequence',
        'Draft',
        { type: 'SequenceResults', id: sequenceId },
      ],
    }),
    /** Ends the leads' runs here and starts them in another sequence. */
    moveLeads: builder.mutation<
      EngageMoveResult,
      {
        sequenceId: string;
        enrollmentIds: string[];
        targetSequenceId: string;
      }
    >({
      query: ({ sequenceId, enrollmentIds, targetSequenceId }) => ({
        url: `/sequences/${sequenceId}/enrollments/move`,
        method: 'POST',
        body: {
          enrollment_ids: enrollmentIds,
          target_sequence_id: targetSequenceId,
        },
      }),
      invalidatesTags: (_result, _error, { sequenceId, targetSequenceId }) => [
        'Enrollment',
        'Task',
        'Sequence',
        'Draft',
        { type: 'SequenceResults', id: sequenceId },
        { type: 'SequenceResults', id: targetSequenceId },
        { type: 'LaunchPreview', id: targetSequenceId },
      ],
    }),
    /** What is known of a lead whose run ended because they left the company. */
    getJobChange: builder.query<
      EngageJobChange,
      { sequenceId: string; enrollmentId: string }
    >({
      query: ({ sequenceId, enrollmentId }) => ({
        url: `/sequences/${sequenceId}/enrollments/${enrollmentId}/job-change`,
        method: 'GET',
      }),
      providesTags: ['Enrollment'],
    }),
    /** Runs the Employment check for the lead (credits); answers 202. */
    findJobChange: builder.mutation<
      EngageJobChange,
      { sequenceId: string; enrollmentId: string }
    >({
      query: ({ sequenceId, enrollmentId }) => ({
        url: `/sequences/${sequenceId}/enrollments/${enrollmentId}/job-change/find`,
        method: 'POST',
      }),
      invalidatesTags: ['Enrollment'],
    }),
    /** Starts a run at the new address in another sequence; free. */
    enrollJobChange: builder.mutation<
      { enrollment_id: string; sequence_id: string },
      { sequenceId: string; enrollmentId: string; targetSequenceId: string }
    >({
      query: ({ sequenceId, enrollmentId, targetSequenceId }) => ({
        url: `/sequences/${sequenceId}/enrollments/${enrollmentId}/job-change/enroll`,
        method: 'POST',
        body: { sequence_id: targetSequenceId },
      }),
      invalidatesTags: ['Enrollment', 'Sequence'],
    }),
    /**
     * A dry run of launching a draft over its waiting leads. Sends nothing.
     * It reads the draft's steps, sender, settings and leads, so a change to
     * any of them invalidates the sequence's LaunchPreview tag.
     */
    getLaunchPreview: builder.query<EngageLaunchPreview, string>({
      query: (sequenceId) => ({
        url: `/sequences/${sequenceId}/launch-preview`,
        method: 'GET',
      }),
      providesTags: (_result, _error, sequenceId) => [
        'Enrollment',
        'Sequence',
        { type: 'LaunchPreview', id: sequenceId },
      ],
    }),
    /** The funnel per sequence, step and sender; `from` and `to` bound the range. */
    getSequenceResults: builder.query<
      EngageSequenceResults,
      { id: string; from?: string; to?: string }
    >({
      query: ({ id, from, to }) => {
        const params = new URLSearchParams();
        if (from) params.append('from', from);
        if (to) params.append('to', to);
        const qs = params.toString();
        return {
          url: `/sequences/${id}/results${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, { id }) => [
        { type: 'SequenceResults', id },
      ],
    }),

    /** The same counts per local day of `tz`, for the reply chart. */
    getSequenceResultsTimeline: builder.query<
      EngageResultsTimeline,
      { id: string; tz: string; from?: string; to?: string }
    >({
      query: ({ id, tz, from, to }) => {
        const params = new URLSearchParams({ tz });
        if (from) params.append('from', from);
        if (to) params.append('to', to);
        return {
          url: `/sequences/${id}/results/timeline?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, { id }) => [
        { type: 'SequenceResults', id },
      ],
    }),

    getCluSampleLeads: builder.query<EngageCluSampleLead[], string>({
      query: (id) => ({
        url: `/sequences/${id}/clu-samples/leads`,
        method: 'GET',
      }),
    }),
    writeCluSample: builder.mutation<
      EngageCluSample,
      {
        id: string;
        enrollment_id: string;
        order_index: number;
        sent: EngageCluSampleSent[];
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/sequences/${id}/clu-samples`,
        method: 'POST',
        body,
      }),
      // Samples do not change the sequence or any of its drafts.
    }),

    // Clu drafts: the emails Clu wrote, waiting for a member or decided.
    /** Newest first, one page at a time; each filter set is its own list. */
    listDrafts: builder.infiniteQuery<
      EngageDraftPage,
      {
        /** `sent` is an approved draft whose email went out. */
        status?: EngageDraftStatus | 'sent';
        kind?: EngageDraft['kind'];
        sequence_id?: string;
        q?: string;
        /** Page size; 50 by default. */
        limit?: number;
      },
      string
    >({
      infiniteQueryOptions: {
        initialPageParam: '',
        getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const params = new URLSearchParams();
        if (queryArg.status) params.append('status', queryArg.status);
        if (queryArg.kind) params.append('kind', queryArg.kind);
        if (queryArg.sequence_id)
          params.append('sequence_id', queryArg.sequence_id);
        if (queryArg.q) params.append('q', queryArg.q);
        params.append('limit', String(queryArg.limit ?? 50));
        if (pageParam) params.append('cursor', pageParam);
        return {
          url: `/sequences/drafts?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Draft'],
    }),
    /** One draft with what it was written with (`lead_state.writing`). */
    getDraft: builder.query<EngageDraftDetail, string>({
      query: (id) => ({ url: `/sequences/drafts/${id}`, method: 'GET' }),
      providesTags: ['Draft'],
    }),
    /**
     * Every draft waiting or blocked across the org's sequences, oldest first
     * and without evidence: one short row each, so a page is cheap. The Tasks
     * page's Review category reads it; deciding is `decideDrafts`.
     */
    listPendingDrafts: builder.infiniteQuery<
      EngagePendingDraftPage,
      {
        status?: 'waiting' | 'blocked';
        sequence_id?: string;
        /** Only the sequences the caller owns. */
        mine?: boolean;
        /** Page size; 50 by default. */
        limit?: number;
      } | void,
      string
    >({
      infiniteQueryOptions: {
        initialPageParam: '',
        getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const params = new URLSearchParams();
        if (queryArg?.status) params.append('status', queryArg.status);
        if (queryArg?.sequence_id)
          params.append('sequence_id', queryArg.sequence_id);
        if (queryArg?.mine) params.append('mine', 'true');
        params.append('limit', String(queryArg?.limit ?? 50));
        if (pageParam) params.append('cursor', pageParam);
        return {
          url: `/sequences/drafts/pending?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Draft'],
    }),
    /**
     * Sequence owner or org admin. Approving releases the runs the drafts hold;
     * a 409 `drafts_changed` means the drafts differ from `expected_count`.
     */
    decideDrafts: builder.mutation<
      EngageDecideDraftsResult,
      EngageDecideDraftsInput
    >({
      query: (body) => ({
        url: `/sequences/drafts/decide`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Draft', 'Enrollment'],
    }),

    // Email accounts (Unipile hosted-auth connect)
    listEmailAccounts: builder.query<
      EngageMailbox[],
      { organizationId?: string; userId?: string } | void
    >({
      query: (arg) => ({
        url: `/email-accounts`,
        method: 'GET',
        ...(arg?.organizationId
          ? { headers: expectedOrganizationHeaders(arg.organizationId) }
          : {}),
      }),
      providesTags: ['Mailbox'],
    }),
    connectEmailAccount: builder.mutation<
      EngageConnectLink,
      {
        provider: 'google' | 'microsoft' | 'both' | 'linkedin';
        /** The sign-in opens in a window that closes itself on return. */
        popup?: boolean;
      }
    >({
      query: (body) => ({
        url: `/email-accounts/connect`,
        method: 'POST',
        body,
      }),
    }),
    // How the latest hosted sign-in ended; `pending` until it has.
    getConnectOutcome: builder.query<
      EngageConnectOutcome,
      { since: number; attempt_id?: string }
    >({
      query: ({ since, attempt_id }) => ({
        url: `/email-accounts/connect/outcome`,
        method: 'GET',
        params: { since, ...(attempt_id ? { attempt_id } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    reconnectEmailAccount: builder.mutation<EngageMailbox, string>({
      query: (id) => ({
        url: `/email-accounts/${id}/reconnect`,
        method: 'POST',
      }),
      invalidatesTags: mailboxChanged,
    }),
    // LinkedIn signs in with credentials (Unipile custom auth), not hosted auth.
    connectLinkedIn: builder.mutation<
      LinkedInConnectResult,
      LinkedInConnectInput
    >({
      query: (body) => ({
        url: `/email-accounts/linkedin/connect`,
        method: 'POST',
        body,
      }),
      invalidatesTags: linkedInConnected,
    }),
    solveLinkedInCheckpoint: builder.mutation<
      LinkedInConnectResult,
      { session: string; code: string }
    >({
      query: (body) => ({
        url: `/email-accounts/linkedin/checkpoint`,
        method: 'POST',
        body,
      }),
      invalidatesTags: linkedInConnected,
    }),
    // A POST: it can finish the connect (IN_APP_VALIDATION approved on the phone).
    checkLinkedInCheckpoint: builder.mutation<
      LinkedInConnectResult,
      { session: string }
    >({
      query: (body) => ({
        url: `/email-accounts/linkedin/checkpoint/status`,
        method: 'POST',
        body,
      }),
      invalidatesTags: linkedInConnected,
    }),
    setAccountShared: builder.mutation<
      EngageMailbox,
      { id: string; shared: boolean }
    >({
      query: ({ id, shared }) => ({
        url: `/email-accounts/${id}/shared`,
        method: 'PUT',
        body: { shared },
      }),
      invalidatesTags: mailboxChanged,
    }),
    setAccountLimit: builder.mutation<
      EngageMailbox,
      { id: string; daily_limit: number }
    >({
      query: ({ id, daily_limit }) => ({
        url: `/email-accounts/${id}/limit`,
        method: 'PUT',
        body: { daily_limit },
      }),
      invalidatesTags: mailboxChanged,
    }),
    // Tagged, so the Remove dialog never warns from a stale count.
    accountUsage: builder.query<{ active_sequence_count: number }, string>({
      query: (id) => ({ url: `/email-accounts/${id}/usage`, method: 'GET' }),
      providesTags: ['Sequence', 'Mailbox'],
    }),
    mailboxSequences: builder.query<EngageMailboxSequence[], string>({
      query: (id) => ({
        url: `/email-accounts/${id}/sequences`,
        method: 'GET',
      }),
      providesTags: ['Sequence', 'Mailbox'],
    }),
    removeEmailAccount: builder.mutation<void, string>({
      query: (id) => ({ url: `/email-accounts/${id}`, method: 'DELETE' }),
      invalidatesTags: mailboxChanged,
    }),
    // Sender settings: per-connection switches + signature, per-member defaults,
    // and the admin team view. The team list carries the Mailbox tag too, so
    // any connection change refreshes it.
    updateEmailAccount: builder.mutation<
      EngageMailbox,
      EngageMailboxSettingsInput
    >({
      query: ({ id, ...body }) => ({
        url: `/email-accounts/${id}`,
        method: 'PATCH',
        body,
      }),
      // Un-sharing takes it off teammates' sequences and unpins their people.
      invalidatesTags: (_result, _error, { shared }) =>
        shared === false
          ? [...mailboxChanged, 'Sequence', 'Enrollment']
          : [...mailboxChanged],
      // Switches and steppers move at once and roll back if the save fails, in
      // both places a connection is shown (my list, the admin team view).
      async onQueryStarted(
        { id, resume, ...input },
        { dispatch, queryFulfilled }
      ) {
        const apply = (mailbox: EngageMailbox | undefined) => {
          if (!mailbox) return;
          Object.assign(mailbox, input);
          if (resume) {
            mailbox.paused_reason = null;
            mailbox.paused_until = null;
          }
        };
        const patches = [
          dispatch(
            engageApi.util.updateQueryData(
              'listEmailAccounts',
              undefined,
              (draft) => apply(draft.find((mailbox) => mailbox.id === id))
            )
          ),
          dispatch(
            engageApi.util.updateQueryData(
              'getSenderTeam',
              undefined,
              (draft) => {
                for (const member of draft.members)
                  apply(member.accounts.find((mailbox) => mailbox.id === id));
              }
            )
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),
    /** Reads the sending domain's records again; answers with the connection. */
    checkMailboxDomain: builder.mutation<EngageMailbox, string>({
      query: (id) => ({
        url: `/email-accounts/${id}/domain-check`,
        method: 'POST',
      }),
      invalidatesTags: mailboxChanged,
    }),
    getSenderSettings: builder.query<EngageSenderSettings, void>({
      query: () => ({ url: `/email-accounts/sender-settings`, method: 'GET' }),
      providesTags: ['SenderSettings'],
    }),
    updateSenderSettings: builder.mutation<
      EngageSenderSettings,
      EngageSenderSettingsInput
    >({
      query: (body) => ({
        url: `/email-accounts/sender-settings`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SenderSettings'],
    }),
    getSenderTeam: builder.query<{ members: EngageTeamSender[] }, void>({
      query: () => ({ url: `/email-accounts/team`, method: 'GET' }),
      providesTags: ['Mailbox', 'SenderSettings'],
    }),
    updateMemberSenderSettings: builder.mutation<
      EngageSenderSettings,
      EngageSenderSettingsInput & { userId: string }
    >({
      query: ({ userId, ...body }) => ({
        url: `/email-accounts/sender-settings/${userId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SenderSettings'],
    }),
    /** Stores a picture for a signature; answers the address it is served at. */
    uploadSignatureImage: builder.mutation<EngageSignatureImage, File>({
      query: (file) => {
        const form = new FormData();
        form.append('file', file);
        return {
          url: `/email-accounts/signature-images`,
          method: 'POST',
          body: form,
        };
      },
    }),

    // Tasks
    /**
     * Finished work, newest first, one page at a time. Each completion window
     * is its own infinite query, so a new window starts over and an
     * invalidation refetches every loaded page from the first.
     */
    listCompletedTasks: builder.infiniteQuery<
      EngageTask[],
      {
        organizationId?: string;
        userId?: string;
        limit: number;
        completed_from?: string;
        completed_to?: string;
      },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        // Another page is likely while the last one came back full.
        getNextPageParam: (lastPage, _pages, lastOffset, _offsets, arg) =>
          lastPage.length < arg.limit ? undefined : lastOffset + arg.limit,
      },
      query: ({ queryArg, pageParam }) => {
        const params = new URLSearchParams();
        params.append('status', 'completed');
        params.append('order', 'newest');
        params.append('limit', String(queryArg.limit));
        if (pageParam) params.append('offset', String(pageParam));
        if (queryArg.completed_from)
          params.append('completed_from', queryArg.completed_from);
        if (queryArg.completed_to)
          params.append('completed_to', queryArg.completed_to);
        // The window is in the rep's calendar days, not UTC ones.
        if (queryArg.completed_from || queryArg.completed_to)
          params.append('tz', browserTimeZone());
        return {
          url: `/tasks?${params.toString()}`,
          method: 'GET',
          ...(queryArg.organizationId && {
            headers: expectedOrganizationHeaders(queryArg.organizationId),
          }),
        };
      },
      providesTags: ['Task'],
    }),
    listTasks: builder.query<
      EngageTask[],
      {
        organizationId?: string;
        userId?: string;
        assignee?: string;
        status?: string;
        sequence_id?: string;
        limit?: number;
        /** 'newest' returns finished work most recent first; 'rank' is My tasks. */
        order?: 'newest' | 'rank';
        /** The caller's IANA zone: the ranking's "today" is their calendar day. */
        tz?: string;
      } | void
    >({
      keepUnusedDataFor: LIST_KEEP_SECONDS,
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg?.tz) params.append('tz', arg.tz);
        if (arg?.assignee) params.append('assignee', arg.assignee);
        if (arg?.status) params.append('status', arg.status);
        if (arg?.sequence_id) params.append('sequence_id', arg.sequence_id);
        if (arg?.limit) params.append('limit', String(arg.limit));
        if (arg?.order) params.append('order', arg.order);
        const qs = params.toString();
        return {
          url: `/tasks${qs ? `?${qs}` : ''}`,
          method: 'GET',
          ...(arg?.organizationId && {
            headers: expectedOrganizationHeaders(arg.organizationId),
          }),
        };
      },
      providesTags: ['Task'],
    }),
    /** One task, whichever list it is in: a link from a CRM task opens it. */
    getTask: builder.query<
      EngageTask,
      string | { id: string; organizationId?: string; userId?: string }
    >({
      query: (arg) => ({
        url: `/tasks/${typeof arg === 'string' ? arg : arg.id}`,
        method: 'GET',
        ...(typeof arg !== 'string' &&
          arg.organizationId && {
            headers: expectedOrganizationHeaders(arg.organizationId),
          }),
      }),
      providesTags: ['Task'],
    }),
    /**
     * Owner, priority or due date for many tasks at once. Tasks the caller may
     * not change come back in `skipped` with a reason; the rest are updated.
     */
    bulkUpdateTasks: builder.mutation<
      EngageBulkTaskResult,
      {
        task_ids: string[];
        assignee_user_id?: string;
        priority?: EngageTaskPriority;
        due_date?: string;
        time24?: string;
      }
    >({
      query: (body) => ({ url: '/tasks/bulk', method: 'POST', body }),
      invalidatesTags: ['Task'],
    }),
    updateTask: builder.mutation<
      EngageTask,
      {
        id: string;
        status?: string;
        outcome?: string;
        notes?: string;
        assignee_user_id?: string;
        due_date?: string;
        time24?: string;
        priority?: EngageTaskPriority;
        /** The rep's wording of the script for this task; '' restores the step's. */
        instructions?: string;
        /** Call log, sent when a call task is completed. */
        call_phone?: string;
        call_started_at?: string;
        call_duration_seconds?: number;
        /** The Cleanlist call the task is completed with (native dialer). */
        call_id?: string;
        /**
         * The caller already shows the change in the cached rows (an inline
         * edit in the table). Nothing is refetched, so the row keeps its place
         * until the next refresh instead of jumping to a new rank.
         */
        silent?: boolean;
      }
    >({
      query: ({ id, ...rest }) => {
        // `silent` is for the cache, not the API.
        const body: Record<string, unknown> = { ...rest };
        delete body.silent;
        return { url: `/tasks/${id}`, method: 'PATCH', body };
      },
      // A completed call can end the lead's run, which the Leads tab shows.
      invalidatesTags: (_result, _error, arg) =>
        arg.silent ? [] : ['Task', 'Enrollment'],
    }),
    listTaskAgents: builder.query<
      EngageTaskAgent[],
      { organizationId?: string; userId?: string } | void
    >({
      query: (arg) => ({
        url: `/tasks/agents`,
        method: 'GET',
        ...(arg?.organizationId && {
          headers: expectedOrganizationHeaders(arg.organizationId),
        }),
      }),
      transformResponse: (response: { agents: EngageTaskAgent[] }) =>
        response.agents,
    }),
    getTaskInsights: builder.query<
      EngageTaskInsights,
      string | { id: string; organizationId?: string; userId?: string }
    >({
      query: (arg) => ({
        url: `/tasks/${typeof arg === 'string' ? arg : arg.id}/insights`,
        method: 'GET',
        ...(typeof arg !== 'string' &&
          arg.organizationId && {
            headers: expectedOrganizationHeaders(arg.organizationId),
          }),
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'TaskInsights', id: typeof arg === 'string' ? arg : arg.id },
      ],
    }),
    runTaskAgent: builder.mutation<
      { run_id: string; lead_list_id: string },
      { task_id: string; agent_key: string; request_id: string }
    >({
      query: ({ task_id, ...body }) => ({
        url: `/tasks/${task_id}/agents/run`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { task_id }) => [
        { type: 'TaskInsights', id: task_id },
      ],
    }),
    /**
     * The play button: one agent on the leads of many tasks. No `task_ids`
     * means every open task assigned to the caller. `dry_run` only counts and
     * prices. Leads that already have a result are skipped, never charged.
     */
    runAgentOnTasks: builder.mutation<
      EngageRunManyResult,
      {
        agent_key: string;
        task_ids?: string[];
        dry_run?: boolean;
        request_id?: string;
        rescore_stale?: boolean;
        max_credits?: number;
        organizationId?: string;
      }
    >({
      query: ({ organizationId, ...body }) => ({
        url: `/tasks/agents/run-all`,
        method: 'POST',
        body,
        ...(organizationId && {
          headers: expectedOrganizationHeaders(organizationId),
        }),
      }),
      invalidatesTags: (_result, _error, { dry_run }) =>
        dry_run ? [] : ['TaskInsights', 'Task'],
    }),
    /** The organization's standard sending schedule; new sequences start from it. */
    getScheduleSettings: builder.query<EngageScheduleSettings, void>({
      query: () => ({ url: `/tasks/schedule-settings`, method: 'GET' }),
      providesTags: ['OrgSchedule'],
    }),
    saveScheduleSettings: builder.mutation<
      EngageScheduleSettings,
      { schedule: EngageScheduleInput | null }
    >({
      query: (body) => ({
        url: `/tasks/schedule-settings`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['OrgSchedule'],
    }),
    /** What replies and bounces do across the organization's sequences. */
    getSequencingSettings: builder.query<
      EngageSequencingSettings,
      { organizationId?: string; userId?: string } | void
    >({
      query: (scope) => ({
        url: `/tasks/sequencing-settings`,
        method: 'GET',
        ...(scope?.organizationId && {
          headers: expectedOrganizationHeaders(scope.organizationId),
        }),
      }),
      providesTags: ['EngageSequencingSettings'],
    }),
    /** Admin only. Answers with the settings as saved. */
    updateSequencingSettings: builder.mutation<
      EngageSequencingSettings,
      EngageSequencingSettingsInput & { organizationId?: string }
    >({
      query: ({ organizationId, ...body }) => ({
        url: `/tasks/sequencing-settings`,
        method: 'PUT',
        body,
        ...(organizationId && {
          headers: expectedOrganizationHeaders(organizationId),
        }),
      }),
      // Stopping or resuming all sending changes every sequence's state, and
      // Clu's caps and rules change what a launch estimate says it will spend.
      invalidatesTags: (_result, _error, change) => [
        'EngageSequencingSettings',
        ...(change.sending_stopped === undefined
          ? []
          : (['Sequence', 'Enrollment'] as const)),
        ...(Object.keys(change).some((key) => key.startsWith('clu_'))
          ? (['LaunchPreview'] as const)
          : []),
      ],
    }),
    /**
     * Admin, Pro and up. Clu writes its first email to one lead under the
     * saved voice samples, style and rules, and nothing comes of it: no
     * draft, charge, hold or send.
     */
    testCluWriting: builder.mutation<
      EngageCluTestResult,
      { enrollment_id: string; goal?: string }
    >({
      query: (body) => ({
        url: `/tasks/sequencing-settings/clu-test`,
        method: 'POST',
        body,
      }),
    }),
    /**
     * The organization's do-not-contact list, newest first, one page at a
     * time. Each search and kind is its own list.
     */
    listDoNotContact: builder.infiniteQuery<
      EngageDncPage,
      { kind?: EngageDncKind; q?: string },
      string
    >({
      infiniteQueryOptions: {
        initialPageParam: '',
        getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const params = new URLSearchParams();
        if (queryArg.kind) params.append('kind', queryArg.kind);
        if (queryArg.q) params.append('q', queryArg.q);
        params.append('limit', '50');
        if (pageParam) params.append('cursor', pageParam);
        return {
          url: `/tasks/do-not-contact?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['DoNotContact'],
    }),
    /** Any member. At most 1,000 entries a call. */
    addDoNotContact: builder.mutation<
      EngageDncAddResult,
      { entries: EngageDncInput[]; source: 'manual' | 'import' }
    >({
      query: (body) => ({
        url: `/tasks/do-not-contact`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DoNotContact'],
    }),
    /** Admin only; unsubscribes, replies and CRM opt-outs answer 409. */
    removeDoNotContact: builder.mutation<void, string>({
      query: (id) => ({
        url: `/tasks/do-not-contact/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DoNotContact'],
    }),
    getCallOutcomeSettings: builder.query<EngageCallOutcomeSettings, void>({
      query: () => ({ url: `/tasks/call-outcome-settings`, method: 'GET' }),
      providesTags: ['CallOutcomes'],
    }),
    saveCallOutcomeSettings: builder.mutation<
      EngageCallOutcomeSettings,
      {
        source: 'cleanlist' | 'crm' | 'dialer';
        crm?: string | null;
        /** With the dialer source the server reads the dialer's own list. */
        dialer?: 'trellus';
        object?: string | null;
        field?: string | null;
        field_label?: string | null;
        outcomes?: EngageCallOutcomeOption[];
      }
    >({
      query: (body) => ({
        url: `/tasks/call-outcome-settings`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CallOutcomes'],
    }),
    listCrmCallFields: builder.query<EngageCrmCallField[], string>({
      query: (crm) => ({
        url: `/tasks/call-outcome-fields?crm=${encodeURIComponent(crm)}`,
        method: 'GET',
      }),
      transformResponse: (response: { fields: EngageCrmCallField[] }) =>
        response.fields,
    }),
    /**
     * What sequence activity is written to the CRM, for the Activities tab.
     * One cache entry per org.
     */
    getCrmActivitySettings: builder.query<
      EngageCrmActivitySettingsRead,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `/tasks/crm-activity-settings`,
        method: 'GET',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'CrmActivitySettings', id: organizationId },
      ],
    }),
    /** Admin only: a setting left out keeps its stored value. */
    saveCrmActivitySettings: builder.mutation<
      EngageCrmActivitySettingsRead,
      { organizationId: string; body: Partial<EngageCrmActivitySettings> }
    >({
      query: ({ organizationId, body }) => ({
        url: `/tasks/crm-activity-settings`,
        method: 'PUT',
        body,
        headers: expectedOrganizationHeaders(organizationId),
      }),
      // The fields a CRM needs follow the person rows, so its header may
      // change with a save.
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'CrmActivitySettings', id: organizationId },
        { type: 'CrmActivitySetup', id: organizationId },
      ],
    }),
    /**
     * One CRM's state for the tab's header, with the Salesforce Task Type and
     * record type values the selects offer.
     */
    getCrmActivitySetup: builder.query<
      EngageCrmActivitySetup,
      { organizationId: string; crm: EngageCrm }
    >({
      query: ({ organizationId, crm }) => ({
        url: `/tasks/crm-activity-setup?crm=${encodeURIComponent(crm)}`,
        method: 'GET',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'CrmActivitySetup', id: organizationId },
      ],
    }),
    /**
     * Admin only: create the fields sequence activity is written with, where
     * missing. Answers with the CRM's state after.
     */
    setUpCrmActivity: builder.mutation<
      EngageCrmActivitySetup,
      { organizationId: string; crm: EngageCrm }
    >({
      query: ({ organizationId, crm }) => ({
        url: `/tasks/crm-activity-setup`,
        method: 'POST',
        body: { crm },
        headers: expectedOrganizationHeaders(organizationId),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        let setup: EngageCrmActivitySetup;
        try {
          ({ data: setup } = await queryFulfilled);
        } catch {
          return;
        }
        const { crm, state, missing_scopes, task_types, record_types } = setup;
        dispatch(
          engageApi.util.upsertQueryData('getCrmActivitySetup', args, {
            crm,
            state,
            missing_scopes,
            task_types,
            record_types,
          })
        );
      },
    }),
    /** One page of the outbox for one CRM, newest first. */
    listCrmActivities: builder.query<
      EngageCrmActivityPage,
      {
        organizationId: string;
        crm: EngageCrm;
        state?: EngageCrmActivityState;
        cursor?: string;
        limit: number;
      }
    >({
      query: ({ organizationId, crm, state, cursor, limit }) => {
        const params = new URLSearchParams();
        params.append('crm', crm);
        if (state) params.append('state', state);
        if (cursor) params.append('cursor', cursor);
        params.append('limit', String(limit));
        return {
          url: `/tasks/crm-activities?${params.toString()}`,
          method: 'GET',
          headers: expectedOrganizationHeaders(organizationId),
        };
      },
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'CrmActivity', id: organizationId },
      ],
    }),
    /** Admin only: Failed rows go back to the drain. */
    retryCrmActivities: builder.mutation<
      { retried: string[] },
      { organizationId: string; ids: string[] }
    >({
      query: ({ organizationId, ids }) => ({
        url: `/tasks/crm-activities/retry`,
        method: 'POST',
        body: { ids },
        headers: expectedOrganizationHeaders(organizationId),
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'CrmActivity', id: organizationId },
      ],
    }),
    /** The CRM users refused as inactive owners, for User Mapping to flag. */
    getCrmActivityInactiveOwners: builder.query<
      EngageCrmInactiveOwners,
      { organizationId: string; crm: EngageCrm }
    >({
      query: ({ organizationId, crm }) => ({
        url: `/tasks/crm-activities/inactive-owners?crm=${encodeURIComponent(crm)}`,
        method: 'GET',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'CrmActivity', id: organizationId },
      ],
    }),
    createTask: builder.mutation<
      EngageTask,
      {
        enrollment_id: string;
        type: 'Call' | 'Manual';
        due_date: string;
        instructions?: string;
        priority?: EngageTaskPriority;
      }
    >({
      query: (body) => ({ url: `/tasks`, method: 'POST', body }),
      invalidatesTags: ['Task'],
    }),
    searchTaskLeads: builder.query<
      EngageTaskLead[],
      string | { q: string; organizationId?: string; userId?: string }
    >({
      query: (arg) => ({
        url: `/tasks/leads?q=${encodeURIComponent(typeof arg === 'string' ? arg : arg.q)}`,
        method: 'GET',
        ...(typeof arg !== 'string' &&
          arg.organizationId && {
            headers: expectedOrganizationHeaders(arg.organizationId),
          }),
      }),
      transformResponse: (response: { leads: EngageTaskLead[] }) =>
        response.leads,
      providesTags: ['Enrollment'],
    }),
    createFollowUp: builder.mutation<
      EngageTask,
      {
        task_id: string;
        type: 'Call' | 'Manual';
        due_date: string;
        instructions?: string;
        priority?: EngageTaskPriority;
      }
    >({
      query: ({ task_id, ...body }) => ({
        url: `/tasks/${task_id}/follow-ups`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Task'],
    }),
    getTaskAccountCrm: builder.query<
      EngageTaskAccountCrm,
      string | { id: string; organizationId?: string; userId?: string }
    >({
      query: (arg) => ({
        url: `/tasks/${typeof arg === 'string' ? arg : arg.id}/account/crm`,
        method: 'GET',
        ...(typeof arg !== 'string' &&
          arg.organizationId && {
            headers: expectedOrganizationHeaders(arg.organizationId),
          }),
      }),
      // Each read spends CRM calls from the daily budget: keep it while the
      // rep moves between tasks instead of asking again.
      keepUnusedDataFor: 600,
    }),
    getTaskAccount: builder.query<
      EngageTaskAccount,
      string | { id: string; organizationId?: string; userId?: string }
    >({
      query: (arg) => ({
        url: `/tasks/${typeof arg === 'string' ? arg : arg.id}/account`,
        method: 'GET',
        ...(typeof arg !== 'string' &&
          arg.organizationId && {
            headers: expectedOrganizationHeaders(arg.organizationId),
          }),
      }),
      providesTags: ['Task', 'Enrollment'],
    }),
    getEnrollmentTimeline: builder.query<
      EngageTimelineEvent[],
      {
        sequence_id: string;
        enrollment_id: string;
        organizationId?: string;
        userId?: string;
      }
    >({
      query: ({ sequence_id, enrollment_id, organizationId }) => ({
        url: `/sequences/${sequence_id}/enrollments/${enrollment_id}/timeline`,
        method: 'GET',
        ...(organizationId && {
          headers: expectedOrganizationHeaders(organizationId),
        }),
      }),
      transformResponse: (response: { events: EngageTimelineEvent[] }) =>
        response.events,
      providesTags: ['Task', 'Thread', 'Enrollment'],
    }),
    getCallOutcomes: builder.query<
      EngageCallOutcome[],
      { organizationId?: string; userId?: string } | void
    >({
      query: (arg) => ({
        url: `/tasks/call-outcomes`,
        method: 'GET',
        ...(arg?.organizationId && {
          headers: expectedOrganizationHeaders(arg.organizationId),
        }),
      }),
      transformResponse: (response: { outcomes: EngageCallOutcome[] }) =>
        response.outcomes,
      providesTags: ['CallOutcomes'],
    }),

    // Inbox
    /**
     * Conversations newest first, filtered on the server, one page at a
     * time. Each filter set is its own infinite query, so a filter change
     * starts over and an invalidation refetches every loaded page.
     */
    listThreads: builder.infiniteQuery<
      EngageThread[],
      EngageThreadFilters & {
        needs_reply?: boolean;
        /** Open (the default), snoozed or archived. */
        state?: EngageThreadState;
        limit: number;
      },
      number
    >({
      keepUnusedDataFor: LIST_KEEP_SECONDS,
      infiniteQueryOptions: {
        initialPageParam: 0,
        // Another page is likely while the last one came back full.
        getNextPageParam: (lastPage, _pages, lastOffset, _offsets, arg) =>
          lastPage.length < arg.limit ? undefined : lastOffset + arg.limit,
      },
      query: ({ queryArg, pageParam }) => {
        const params = new URLSearchParams();
        if (queryArg.sequence_id)
          params.append('sequence_id', queryArg.sequence_id);
        if (queryArg.needs_reply !== undefined)
          params.append('needs_reply', String(queryArg.needs_reply));
        if (queryArg.mailbox_id)
          params.append('mailbox_id', queryArg.mailbox_id);
        if (queryArg.since) {
          // A day in the rep's calendar, not a UTC one.
          params.append('since', queryArg.since);
          params.append('tz', browserTimeZone());
        }
        if (queryArg.label) params.append('label', queryArg.label);
        if (queryArg.assignee) params.append('assignee', queryArg.assignee);
        if (queryArg.q?.trim()) params.append('q', queryArg.q.trim());
        if (queryArg.state) params.append('state', queryArg.state);
        params.append('limit', String(queryArg.limit));
        if (pageParam) params.append('offset', String(pageParam));
        return {
          url: `/inbox/threads?${params.toString()}`,
          method: 'GET',
          ...(queryArg.organizationId
            ? {
                headers: {
                  'X-Cleanlist-Expected-Org': queryArg.organizationId,
                },
              }
            : {}),
        };
      },
      providesTags: ['Thread'],
    }),
    /**
     * How many conversations each view holds, under the filters the list has,
     * so the tabs count all of them and not the page that is loaded.
     */
    getThreadCounts: builder.query<EngageThreadCounts, EngageThreadFilters>({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg.sequence_id) params.append('sequence_id', arg.sequence_id);
        if (arg.mailbox_id) params.append('mailbox_id', arg.mailbox_id);
        if (arg.since) {
          params.append('since', arg.since);
          params.append('tz', browserTimeZone());
        }
        if (arg.label) params.append('label', arg.label);
        if (arg.assignee) params.append('assignee', arg.assignee);
        if (arg.q?.trim()) params.append('q', arg.q.trim());
        return {
          url: `/inbox/counts?${params.toString()}`,
          method: 'GET',
          ...(arg.organizationId
            ? {
                headers: {
                  'X-Cleanlist-Expected-Org': arg.organizationId,
                },
              }
            : {}),
        };
      },
      providesTags: ['Thread'],
    }),
    /** The sequences a conversation can belong to, by name only. */
    listInboxSequences: builder.query<
      { id: string; name: string }[],
      { organizationId?: string; userId?: string } | void
    >({
      query: (arg) => ({
        url: '/inbox/sequences',
        method: 'GET',
        ...(arg && arg.organizationId
          ? { headers: { 'X-Cleanlist-Expected-Org': arg.organizationId } }
          : {}),
      }),
      providesTags: [{ type: 'Sequence', id: 'LIST' }],
    }),
    /** One conversation, as the list reads it. */
    getThread: builder.query<
      EngageThread,
      string | { id: string; organizationId?: string; userId?: string }
    >({
      query: (arg) => ({
        url: `/inbox/threads/${typeof arg === 'string' ? arg : arg.id}`,
        method: 'GET',
        ...(typeof arg !== 'string' && arg.organizationId
          ? { headers: { 'X-Cleanlist-Expected-Org': arg.organizationId } }
          : {}),
      }),
      providesTags: ['Thread'],
    }),
    /** The lead's company beside a conversation, as a task's account reads. */
    getThreadAccount: builder.query<
      EngageTaskAccount,
      string | { id: string; organizationId?: string; userId?: string }
    >({
      query: (arg) => ({
        url: `/inbox/threads/${typeof arg === 'string' ? arg : arg.id}/account`,
        method: 'GET',
        ...(typeof arg !== 'string' &&
          arg.organizationId && {
            headers: expectedOrganizationHeaders(arg.organizationId),
          }),
      }),
      providesTags: ['Thread', 'Enrollment'],
    }),
    /** The account's CRM side beside a conversation; each read spends CRM calls. */
    getThreadAccountCrm: builder.query<
      EngageTaskAccountCrm,
      string | { id: string; organizationId?: string; userId?: string }
    >({
      query: (arg) => ({
        url: `/inbox/threads/${typeof arg === 'string' ? arg : arg.id}/account/crm`,
        method: 'GET',
        ...(typeof arg !== 'string' &&
          arg.organizationId && {
            headers: expectedOrganizationHeaders(arg.organizationId),
          }),
      }),
      keepUnusedDataFor: 600,
    }),
    /**
     * The one click for a meeting request: opens the caller's task to send the
     * invite and answers with the reply that proposes the lead's time. It
     * sends nothing.
     */
    bookThreadMeeting: builder.mutation<
      EngageBookMeetingResult,
      { id: string; due_date: string }
    >({
      query: ({ id, due_date }) => ({
        url: `/inbox/threads/${id}/book-meeting`,
        method: 'POST',
        body: { due_date },
      }),
      invalidatesTags: ['Task'],
    }),
    /** Who answers a conversation, when it comes back, or put away. */
    updateThread: builder.mutation<
      EngageThread,
      { id: string } & EngageThreadChange
    >({
      query: ({ id, ...body }) => ({
        url: `/inbox/threads/${id}`,
        method: 'PATCH',
        body,
      }),
      // The open conversation reads the change at once, before the lists
      // refetch to move it between Needs reply, Snoozed and Archived.
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        let thread: EngageThread;
        try {
          ({ data: thread } = await queryFulfilled);
        } catch {
          return;
        }
        for (const args of engageApi.util.selectCachedArgsForQuery(
          getState(),
          'listThreads'
        )) {
          dispatch(
            engageApi.util.updateQueryData('listThreads', args, (draft) => {
              for (const page of draft.pages)
                for (const [index, row] of page.entries())
                  if (row.id === id) page[index] = thread;
            })
          );
        }
      },
      invalidatesTags: ['Thread'],
    }),
    /** One lead's events across every sequence they have run in. */
    getThreadTimeline: builder.query<
      EngageTimelineEvent[],
      string | { id: string; organizationId?: string; userId?: string }
    >({
      query: (arg) => ({
        url: `/inbox/threads/${typeof arg === 'string' ? arg : arg.id}/timeline`,
        method: 'GET',
        ...(typeof arg !== 'string' && arg.organizationId
          ? { headers: { 'X-Cleanlist-Expected-Org': arg.organizationId } }
          : {}),
      }),
      transformResponse: (response: { events: EngageTimelineEvent[] }) =>
        response.events,
      providesTags: ['Task', 'Thread', 'Enrollment'],
    }),
    saveThreadDraft: builder.mutation<
      EngageThread,
      { id: string; reply_draft: string }
    >({
      query: ({ id, reply_draft }) => ({
        url: `/inbox/threads/${id}/draft`,
        method: 'PUT',
        body: { reply_draft },
      }),
      // A saved draft changes one field of one thread: patch it into every
      // loaded list instead of refetching them all.
      async onQueryStarted(
        { id, reply_draft },
        { dispatch, getState, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        for (const args of engageApi.util.selectCachedArgsForQuery(
          getState(),
          'listThreads'
        )) {
          dispatch(
            engageApi.util.updateQueryData('listThreads', args, (draft) => {
              for (const page of draft.pages)
                for (const thread of page)
                  if (thread.id === id) thread.reply = reply_draft;
            })
          );
        }
        // The conversation a Reply task shows reads its draft the same way.
        for (const args of engageApi.util.selectCachedArgsForQuery(
          getState(),
          'getThread'
        )) {
          dispatch(
            engageApi.util.updateQueryData('getThread', args, (draft) => {
              if (draft.id === id) draft.reply = reply_draft;
            })
          );
        }
      },
    }),
    replyThread: builder.mutation<
      EngageThread,
      { id: string; body: string; draft_id?: string; request_id?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/inbox/threads/${id}/reply`,
        method: 'POST',
        body,
      }),
      // The open conversation reads our reply at once, before the lists
      // refetch and an answered one leaves Needs reply.
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        let thread: EngageThread;
        try {
          ({ data: thread } = await queryFulfilled);
        } catch {
          return;
        }
        for (const args of engageApi.util.selectCachedArgsForQuery(
          getState(),
          'listThreads'
        )) {
          dispatch(
            engageApi.util.updateQueryData('listThreads', args, (draft) => {
              for (const page of draft.pages)
                for (const [index, row] of page.entries())
                  if (row.id === id) page[index] = thread;
            })
          );
        }
      },
      // Sending a Clu draft approves it, so the record reads it as sent. An
      // answered conversation closes its Reply task, so Tasks reads it too.
      invalidatesTags: ['Thread', 'Draft', 'Task'],
    }),
    /**
     * Clu writes a reply from the approved documents. It fills the reply box
     * and sends nothing. 409 `no_approved_documents`, 402 `no_credits`, 429
     * `capped`.
     */
    draftThreadReply: builder.mutation<EngageThreadDraftResult, string>({
      query: (id) => ({
        url: `/inbox/threads/${id}/clu-draft`,
        method: 'POST',
      }),
    }),
    /**
     * The person asked to stop: their runs in the org stop, and with "Write
     * unsubscribes to CRM" on, the opt-out goes to the CRM whether or not
     * activity logging is on. Answers with the thread as it now reads.
     */
    unsubscribeThread: builder.mutation<EngageThread, string>({
      query: (id) => ({
        url: `/inbox/threads/${id}/unsubscribe`,
        method: 'POST',
      }),
      // The open conversation reads Unsubscribed at once, before the lists
      // refetch for the person's other conversations and stopped runs.
      async onQueryStarted(id, { dispatch, getState, queryFulfilled }) {
        let thread: EngageThread;
        try {
          ({ data: thread } = await queryFulfilled);
        } catch {
          return;
        }
        for (const args of engageApi.util.selectCachedArgsForQuery(
          getState(),
          'listThreads'
        )) {
          dispatch(
            engageApi.util.updateQueryData('listThreads', args, (draft) => {
              for (const page of draft.pages)
                for (const [index, row] of page.entries())
                  if (row.id === id) page[index] = thread;
            })
          );
        }
      },
      invalidatesTags: [
        'Thread',
        'Enrollment',
        'Task',
        'Sequence',
        'CrmActivity',
      ],
    }),

    // Handover (admin only)
    /** Who left, and which mailboxes are down, with the work they left. */
    getHandoverCandidates: builder.query<EngageHandoverCandidates, void>({
      query: () => ({
        url: `/sequences/handover/candidates`,
        method: 'GET',
      }),
      providesTags: ['Handover'],
    }),
    /**
     * Moves a leaver's or a down mailbox's sequences, running leads, open
     * tasks and conversations to a new owner and sender. `dry_run` counts
     * and writes nothing.
     */
    runHandover: builder.mutation<EngageHandoverResult, EngageHandoverInput>({
      query: (body) => ({ url: `/sequences/handover`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { dry_run }) =>
        dry_run
          ? []
          : ['Handover', 'Sequence', 'Enrollment', 'Task', 'Thread', 'Mailbox'],
    }),
  }),
});

export const {
  useLazyGetCluSampleLeadsQuery,
  useWriteCluSampleMutation,
  useGetTaskAccountCrmQuery,
  useCreateTaskMutation,
  useSearchTaskLeadsQuery,
  useGetCallOutcomeSettingsQuery,
  useGetScheduleSettingsQuery,
  useGetTaskInsightsQuery,
  useLazyListCrmCallFieldsQuery,
  useListTaskAgentsQuery,
  useRunAgentOnTasksMutation,
  useRunTaskAgentMutation,
  useSaveCallOutcomeSettingsMutation,
  useSaveScheduleSettingsMutation,
  useGetSequencingSettingsQuery,
  useGetDraftQuery,
  useUpdateSequencingSettingsMutation,
  useTestCluWritingMutation,
  useListDoNotContactInfiniteQuery,
  useAddDoNotContactMutation,
  useRemoveDoNotContactMutation,
  useGetLaunchPreviewQuery,
  useGetSequenceResultsQuery,
  useGetSequenceResultsTimelineQuery,
  useListDraftsInfiniteQuery,
  useListPendingDraftsInfiniteQuery,
  useDecideDraftsMutation,
  useCheckMailboxDomainMutation,
  useCreateFollowUpMutation,
  useGetTaskAccountQuery,
  useGetEnrollmentTimelineQuery,
  useListSequencesQuery,
  useGetSequenceQuery,
  useCreateSequenceMutation,
  useUpdateSequenceMutation,
  useDeleteSequenceMutation,
  useDuplicateSequenceMutation,
  useRemoveSequenceSenderMutation,
  useMailboxSequencesQuery,
  useEditSequenceMutation,
  useSaveSequenceMutation,
  useActivateSequenceMutation,
  usePauseSequenceMutation,
  useResumeSequenceMutation,
  useArchiveSequenceMutation,
  useUnarchiveSequenceMutation,
  useSetSequenceFavoriteMutation,
  useUpdateSequenceSettingsMutation,
  useAddStepMutation,
  useUpdateStepMutation,
  useDeleteStepMutation,
  usePreviewEnrollmentMutation,
  useEnrollMutation,
  useListEnrollmentsQuery,
  useActOnLeadMutation,
  useBulkActOnLeadsMutation,
  useMoveLeadsMutation,
  useGetJobChangeQuery,
  useFindJobChangeMutation,
  useEnrollJobChangeMutation,
  useListEmailAccountsQuery,
  useConnectEmailAccountMutation,
  useGetConnectOutcomeQuery,
  useConnectLinkedInMutation,
  useSolveLinkedInCheckpointMutation,
  useCheckLinkedInCheckpointMutation,
  useAccountUsageQuery,
  useRemoveEmailAccountMutation,
  useUpdateEmailAccountMutation,
  useGetSenderSettingsQuery,
  useUpdateSenderSettingsMutation,
  useGetSenderTeamQuery,
  useUpdateMemberSenderSettingsMutation,
  useUploadSignatureImageMutation,
  useListCompletedTasksInfiniteQuery,
  useListTasksQuery,
  useGetTaskQuery,
  useGetCrmActivitySettingsQuery,
  useSaveCrmActivitySettingsMutation,
  useGetCrmActivitySetupQuery,
  useSetUpCrmActivityMutation,
  useListCrmActivitiesQuery,
  useRetryCrmActivitiesMutation,
  useGetCrmActivityInactiveOwnersQuery,
  useBulkUpdateTasksMutation,
  useUpdateTaskMutation,
  useGetCallOutcomesQuery,
  useListThreadsInfiniteQuery,
  useGetThreadCountsQuery,
  useListInboxSequencesQuery,
  useUpdateThreadMutation,
  useGetThreadTimelineQuery,
  useGetThreadQuery,
  useGetThreadAccountQuery,
  useGetThreadAccountCrmQuery,
  useBookThreadMeetingMutation,
  useSaveThreadDraftMutation,
  useReplyThreadMutation,
  useDraftThreadReplyMutation,
  useUnsubscribeThreadMutation,
  useGetHandoverCandidatesQuery,
  useRunHandoverMutation,
} = engageApi;
